import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBox,
    faBarcode,
    faTag,
    faBuilding,
    faRupeeSign,
    faCalculator,
    faExclamationTriangle,
    faCalendarAlt,
    faSave,
    faTimes,
    faCheckCircle,
    faStore,
    faExchangeAlt,
    faWarehouse,
    faPercent,
    faQrcode,
    faBoxes,
    faLayerGroup,
    faCubes,
    faFileAlt,
    faSpinner,
    faChevronDown,
    faPalette,
    faRuler, 
    faRightLeft,
} from '@fortawesome/free-solid-svg-icons';
// import './ProductsForm.bulk.css';

// ==============================================
// GLOBAL BARCODE SCANNER HOOK (works anywhere in the form)
// ==============================================
const useBarcodeScanner = (onBarcodeScanned, isActive = true) => {
    const bufferRef = useRef('');
    const scanStartedAtRef = useRef(0);
    const lastTimeRef = useRef(0);
    const scanTimerRef = useRef(null);
    const isProcessingRef = useRef(false);
    const isLikelyScanningRef = useRef(false);
    const leakSnapshotRef = useRef(null); // { element, value, selectionStart }

    useEffect(() => {
        if (!isActive) return;

        const SCAN_MAX_INTERVAL_MS = 80;
        const MAX_AVERAGE_INTERVAL_MS = 60;
        const MIN_BARCODE_LENGTH = 4;
        const BUFFER_TIMEOUT_MS = 180;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        // Set a value on a native input in a way React's controlled-input
        // listener still picks up (bypasses React's overridden setter).
        const setNativeValue = (el, value) => {
            if (!el) return;
            if (el.tagName === 'INPUT' && nativeInputValueSetter) {
                nativeInputValueSetter.call(el, value);
            } else {
                el.value = value;
            }
            el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        // Undo the single leaked character (typed before we knew it was a scan)
        const restoreLeak = () => {
            const snap = leakSnapshotRef.current;
            if (snap && snap.element && snap.element.isConnected) {
                setNativeValue(snap.element, snap.value);
                if (snap.selectionStart != null && snap.element.setSelectionRange) {
                    try {
                        snap.element.setSelectionRange(snap.selectionStart, snap.selectionStart);
                    } catch (e) { /* ignore */ }
                }
            }
            leakSnapshotRef.current = null;
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            scanStartedAtRef.current = 0;
            isLikelyScanningRef.current = false;
            leakSnapshotRef.current = null;
        };

        const commitBufferedScan = (event = null) => {
            clearTimeout(scanTimerRef.current);
            const candidate = bufferRef.current.trim();
            const averageInterval = candidate.length > 1 && scanStartedAtRef.current
                ? (lastTimeRef.current - scanStartedAtRef.current) / (candidate.length - 1)
                : Number.MAX_SAFE_INTEGER;
            const isConfirmedScan = isLikelyScanningRef.current &&
                candidate.length >= MIN_BARCODE_LENGTH &&
                averageInterval <= MAX_AVERAGE_INTERVAL_MS;

            if (isConfirmedScan) {
                if (event) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                restoreLeak();

                if (!isProcessingRef.current) {
                    isProcessingRef.current = true;
                    // Bulk handlers resolve immediately, so rapid consecutive
                    // scans are not throttled by an arbitrary timer. Async
                    // single-product lookups remain serialized until complete.
                    Promise.resolve(onBarcodeScanned(candidate))
                        .catch(error => console.error('Barcode handler error:', error))
                        .finally(() => { isProcessingRef.current = false; });
                }
            }

            resetBuffer();
        };

        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey || e.altKey) {
                resetBuffer();
                return;
            }
            if (e.key === 'Shift' || e.key === 'CapsLock') {
                return;
            }

            const now = Date.now();
            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;

            const target = e.target;
            const isEditable = !!target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (e.key === 'Enter' || e.key === 'Tab') {
                if (bufferRef.current) commitBufferedScan(e);
                return;
            }

            // Only buffer single printable characters
            if (e.key.length !== 1) {
                resetBuffer();
                return;
            }

            if (bufferRef.current.length === 0 || delta > SCAN_MAX_INTERVAL_MS) {
                // Possible start of a new sequence — snapshot in case it turns out to be a scan
                bufferRef.current = e.key;
                scanStartedAtRef.current = now;
                isLikelyScanningRef.current = false;
                leakSnapshotRef.current = isEditable ? {
                    element: target,
                    value: target.value,
                    selectionStart: target.selectionStart
                } : null;
            } else {
                // Fast keystroke — this is a scan in progress, block it from typing into the field
                bufferRef.current += e.key;
                isLikelyScanningRef.current = true;
                if (isEditable) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }

            clearTimeout(scanTimerRef.current);
            // Scanners without an Enter/Tab suffix are committed after a short pause.
            scanTimerRef.current = setTimeout(() => commitBufferedScan(), BUFFER_TIMEOUT_MS);
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [onBarcodeScanned, isActive]);
};

// Create translator instance with Urdu translations
const createProductsTranslator = () => {
    let cache = new Map();

    // Predefined translations for Urdu
    const urduTranslations = {
        addProduct: 'محفوظ کریں',
        editProduct: 'ترمیم کریں',
        fillDetails: 'براہ کرم تفصیلات درج کریں',
        productDetails: 'مصنوعات کی تفصیلات',
        productName: 'مصنوعات کا نام',
        productCode: 'مصنوعات کا کوڈ',
        batchNumber: 'بیچ نمبر',
        barcode: 'بارکوڈ',
        category: 'زمرہ',
        company: 'کمپنی / برانڈ',
        color: 'رنگ',
        size: 'سائز',
        unit: 'یونٹ',
        costPrice: 'لاگت قیمت',
        sellingPrice: 'فروخت قیمت',
        quantity: 'مقدار',
        quantityAlert: 'کم اسٹاک الرٹ',
        expiryDate: 'میعاد ختم ہونے کی تاریخ',
        date: 'تاریخ',
        addProductButton: 'محفوظ کریں',
        updateProduct: 'اپ ڈیٹ کریں',
        clearButton: 'صاف کریں',
        adding: 'محفوظ ہو رہا ہے...',
        updating: 'اپ ڈیٹ ہو رہا ہے...',
        productSaved: 'مصنوعات کامیابی سے محفوظ ہو گئی!',
        productUpdated: 'مصنوعات کامیابی سے اپ ڈیٹ ہو گئی!',
        failedAdd: 'مصنوعات محفوظ کرنے میں ناکامی',
        failedUpdate: 'مصنوعات اپ ڈیٹ کرنے میں ناکامی',
        networkError: 'نیٹ ورک کی خرابی',
        nameRequired: 'مصنوعات کا نام ضروری ہے',
        nameTooShort: 'نام کم از کم 2 حروف کا ہونا چاہیے',
        batchRequired: 'بیچ نمبر ضروری ہے',
        batchExists: 'یہ بیچ نمبر پہلے سے موجود ہے',
        productCodeExists: 'یہ مصنوعات کا کوڈ پہلے سے موجود ہے',
        unitRequired: 'یونٹ ضروری ہے',
        costPriceRequired: 'لاگت قیمت ضروری ہے',
        sellingPriceRequired: 'فروخت قیمت ضروری ہے',
        quantityRequired: 'مقدار ضروری ہے',
        alertRequired: 'کم اسٹاک الرٹ ضروری ہے',
        invalidPrice: 'براہ کرم درست قیمت درج کریں',
        invalidQuantity: 'براہ کرم درست مقدار درج کریں',
        expiryPast: 'میعاد ختم ہونے کی تاریخ ماضی میں نہیں ہو سکتی',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        selectShop: 'پہلے دکان منتخب کریں',
        currentShop: 'موجودہ دکان',
        willAddToShop: 'مصنوعات اس دکان میں شامل ہو گی',
        noShopSelected: 'کوئی دکان منتخب نہیں۔ براہ کرم پہلے دکان تبدیل کریں',
        switchShop: 'دکان تبدیل کریں',
        shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
        shopSwitchFailed: 'دکان تبدیل کرنے میں ناکامی',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
        noShops: 'کوئی دکان دستیاب نہیں',
        active: 'فعال',
        current: 'موجودہ',
        profitMargin: 'منافع کا مارجن',
        markup: 'مارک اپ',
        selectCategory: 'زمرہ منتخب کریں',
        selectUnit: 'یونٹ منتخب کریں',
        optional: 'اختیاری',
        enterProductName: 'مصنوعات کا نام درج کریں',
        enterProductCode: 'مصنوعات کا کوڈ درج کریں',
        enterBatchNumber: 'بیچ نمبر درج کریں (اختیاری)',
        barcodeAutoGenerate: 'بارکوڈ خودکار جنریٹ ہو گا',
        enterCompany: 'کمپنی/برانڈ درج کریں',
        zeroPlaceholder: '0.0',
        zeroPlaceholderDecimal: '0.00',
        zeroPlaceholderThreeDecimal: '0.000',
        packingType: 'پیکنگ کی قسم',
        subPackingType: 'ذیلی پیکنگ کی قسم',
        itemsPerPack: 'فی پیک اشیاء',
        unitOfMeasure: 'پیمائش کی اکائی',
        totalQuantity: 'کل مقدار',
        costPerPack: 'فی پیک لاگت',
        sellingPerPack: 'فی پیک فروخت قیمت',
        selectPackingType: 'پیکنگ کی قسم منتخب کریں',
        enterItemsPerPack: 'فی پیک اشیاء کی تعداد درج کریں',
        costPerUnit: 'فی یونٹ لاگت',
        sellPricePerUnit: 'فی یونٹ فروخت قیمت',
        bquantity: 'کل مقدار (بلک)',
        box: 'باکس',
        carton: 'کارٹن',
        crate: 'کریٹ',
        packet: 'پیکٹ',
        bundle: 'بنڈل',
        bottle: 'بوتل',
        can: 'کین',
        piece: 'ٹکڑا',
        '1_liter': '1 لیٹر',
        '2_liter': '2 لیٹر',
        '3_liter': '3 لیٹر',
        '5_liter': '5 لیٹر',
        draftLoaded: 'ڈرافٹ لوڈ ہو گیا',
        draftCleared: 'ڈرافٹ صاف کر دیا گیا',
        draftExists: 'پہلے سے محفوظ ڈرافٹ موجود ہے',
        batchAutoGenerated: 'بیچ نمبر خودکار جنریٹ ہو گیا: ',
        productFound: 'پروڈکٹ مل گیا',
        productNotFound: 'پروڈکٹ نہیں ملا',
        barcodeSet: 'بارکوڈ سیٹ کر دیا گیا',
        scannerActive: 'اسکینر فعال ہے'
    };

    const defaultTexts = {
        addProduct: 'Add Product',
        editProduct: 'Edit Stock',
        fillDetails: 'Fill in the stock details below',
        productDetails: 'Product Details',
        productName: 'Product Name',
        productCode: 'Product Code / Grade',
        batchNumber: 'Batch Number',
        barcode: 'Barcode',
        category: 'Category',
        company: 'Company / Brand',
        color: 'Color',
        size: 'Size',
        unit: 'Unit',
        costPrice: 'Cost Price',
        sellingPrice: 'Selling Price',
        quantity: 'Quantity',
        quantityAlert: 'Low Stock Alert',
        expiryDate: 'Expiry Date',
        date: 'Date',
        addProductButton: 'Save Product',
        updateProduct: 'Update Product',
        clearButton: 'Clear',
        adding: 'Adding...',
        updating: 'Updating...',
        productSaved: 'Product saved successfully!',
        productUpdated: 'Product updated successfully!',
        failedAdd: 'Failed to save product',
        failedUpdate: 'Failed to update product',
        networkError: 'Network error occurred',
        nameRequired: 'Product name is required',
        nameTooShort: 'Name must be at least 2 characters',
        batchRequired: 'Batch number is required',
        batchExists: 'This batch number already exists',
        productCodeExists: 'This product code already exists',
        unitRequired: 'Unit is required',
        costPriceRequired: 'Cost price is required',
        sellingPriceRequired: 'Selling price is required',
        quantityRequired: 'Quantity is required',
        alertRequired: 'Low stock alert quantity is required',
        invalidPrice: 'Please enter a valid price',
        invalidQuantity: 'Please enter a valid quantity',
        expiryPast: 'Expiry date cannot be in the past',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        currentShop: 'Current Shop',
        willAddToShop: 'Product will be added to this shop',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        active: 'Active',
        current: 'Current',
        profitMargin: 'Profit Margin',
        markup: 'Markup',
        selectCategory: 'Select category',
        selectUnit: 'Select unit',
        optional: 'Optional',
        enterProductName: 'Enter product name',
        enterProductCode: 'Enter product code',
        enterBatchNumber: 'Enter batch number (optional)',
        barcodeAutoGenerate: 'Barcode will auto-generate',
        enterCompany: 'Enter company/brand',
        zeroPlaceholder: '0.0',
        zeroPlaceholderDecimal: '0.00',
        zeroPlaceholderThreeDecimal: '0.000',
        packingType: 'Packing Type',
        subPackingType: 'Sub Packing Type',
        itemsPerPack: 'Items Per',
        unitOfMeasure: 'Unit of Measure',
        totalQuantity: 'Total Quantity',
        costPerPack: 'Cost Price',
        sellingPerPack: 'Selling Price',
        selectPackingType: 'Select packing type',
        enterItemsPerPack: 'Enter items per pack',
        costPerUnit: 'Cost',
        sellPricePerUnit: 'Sell price',
        bquantity: 'BQuantity',
        box: 'Box',
        carton: 'Carton',
        crate: 'Crate',
        packet: 'Packet',
        bundle: 'Bundle',
        bottle: 'Bottle',
        can: 'Can',
        piece: 'Piece',
        '1_liter': '1 Liter',
        '2_liter': '2 Liters',
        '3_liter': '3 Liters',
        '5_liter': '5 Liters',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        draftExists: 'A saved draft exists',
        batchAutoGenerated: 'Batch number auto-generated: ',
        productFound: 'Product found!',
        productNotFound: 'Product not found',
        barcodeSet: 'Barcode set',
        scannerActive: 'Scanner Active'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        if (lang === 'ur') return urduTranslations;
        return defaultTexts;
    };

    return { getTranslations };
};

const productsTranslator = createProductsTranslator();

// Helper function to generate batch number from product name - 3 letters + incrementing numbers
const generateBatchNoFromProductName = (productName, existingBatchNos = []) => {
    if (!productName || productName.trim() === '') {
        return '';
    }
    
    let cleanName = productName.trim().toUpperCase();
    cleanName = cleanName.replace(/[^A-Z0-9]/g, '');
    
    if (cleanName.length === 0) {
        cleanName = 'XXX';
    } else if (cleanName.length === 1) {
        cleanName = cleanName + 'XX';
    } else if (cleanName.length === 2) {
        cleanName = cleanName + 'X';
    }
    
    const namePart = cleanName.substring(0, 3);
    let highestNumber = 0;
    const prefix = namePart;
    
    existingBatchNos.forEach(batch => {
        if (batch && batch.startsWith(prefix)) {
            const numericPart = batch.substring(3);
            if (numericPart && /^\d{4}$/.test(numericPart)) {
                const num = parseInt(numericPart, 10);
                if (num > highestNumber) {
                    highestNumber = num;
                }
            }
        }
    });
    
    const nextNumber = highestNumber + 1;
    const numericStr = String(nextNumber).padStart(4, '0');
    return `${namePart}${numericStr}`;
};

// Helper function to generate barcode from product name - EDITABLE
const generateBarcodeFromProductName = (productName, username, shopId) => {
    if (!productName || productName.trim() === '') return '';
    if (!username || !shopId) return '';

    const cleanName = productName.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    let namePart = cleanName.substring(0, 3);
    if (namePart.length < 3) {
        namePart = namePart.padEnd(3, 'X');
    }
    
    let shopPrefix = shopId.toString().substring(0, 2).replace(/[^0-9]/g, '0');
    if (shopPrefix.length < 2) {
        shopPrefix = shopPrefix.padEnd(2, '0');
    }
    
    let userPrefix = username.substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, 'P');
    if (userPrefix.length < 3) {
        userPrefix = userPrefix.padEnd(3, 'P');
    }
    
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let randomPart = '';
    for (let i = 0; i < 4; i++) {
        randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    
    return `${namePart}${randomPart}${shopPrefix}${userPrefix}`;
};

const createInitialBulkForm = () => ({
    date: new Date().toISOString().split('T')[0],
    product_name: '',
    rq: '',
    category: '',
    company: '',
    cost_price_per_pack: '',
    profit_percent: '',
    sell_price_per_unit: ''
});

const generateBulkBatchNo = (productName, shopId, rowIndex) => {
    const cleaned = (productName || 'XXX').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const prefix = (cleaned || 'XXX').substring(0, 3).padEnd(3, 'X');
    const shopPart = String(shopId || '0').replace(/\D/g, '').slice(-3).padStart(3, '0');
    const timePart = Date.now().toString(36).toUpperCase();
    const rowPart = String(rowIndex + 1).padStart(3, '0');
    const randomPart = Math.random().toString(36).slice(2, 7).toUpperCase().padEnd(5, 'X');
    return `${prefix}-${shopPart}-${timePart}-${rowPart}-${randomPart}`;
};

const createBulkRow = (productName, shopId, rowIndex) => ({
    batch_no: generateBulkBatchNo(productName, shopId, rowIndex),
    total_quantity: '1',
    barcode: ''
});

const BULK_PAGE_SIZE = 100;

const formatEditableNumber = (value, decimals = 4) => {
    if (!Number.isFinite(value)) return '';
    return Number(value.toFixed(decimals)).toString();
};

const getStoredOrCalculatedProfitPercent = (product) => {
    if (product?.profit_percent !== null && product?.profit_percent !== undefined && product?.profit_percent !== '') {
        return String(product.profit_percent);
    }

    const cost = parseFloat(product?.cost_per_pack ?? product?.cost_price ?? 0);
    const sell = parseFloat(product?.sell_price_per_unit ?? product?.sell_price ?? 0);
    return cost > 0 ? formatEditableNumber(((sell - cost) / cost) * 100) : '';
};

// Request deduplication helper
const requestTracker = new Map();

function isDuplicateRequest(requestData) {
    const key = JSON.stringify({
        action: requestData.action,
        product_name: requestData.product_name,
        batch_no: requestData.batch_no,
        barcode: requestData.barcode,
        product_code: requestData.product_code,
        shop_id: requestData.shop_id
    });

    const now = Date.now();
    const lastRequest = requestTracker.get(key);

    if (lastRequest && (now - lastRequest) < 5000) {
        return true;
    }

    requestTracker.set(key, now);

    for (const [k, time] of requestTracker.entries()) {
        if (now - time > 10000) {
            requestTracker.delete(k);
        }
    }

    return false;
}

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            case 'info':
                return faCheckCircle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            case 'info':
                return 'Info';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()}>
                <div className="ozui_modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{translations.loadingShops || 'Loading shops...'}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{translations.noShops || 'No shops available'}</div>
                    ) : (
                        shops.map((shop) => (
                            <div 
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {translations.current || 'Current'}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const CosmeticsProducts = ({ productId = null, onSuccess = null }) => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return user?.language || localStorage.getItem('ourzest_language') || 'en';
    });

    // Auto-focus ref for Product Name field
    const productNameRef = useRef(null);

    // ==============================================
    // STATE DECLARATIONS - All state must be declared before any functions that use them
    // ==============================================
    
    // Form state
    const [formData, setFormData] = useState({
        record_id: productId || null,
        date: new Date().toISOString().split('T')[0],
        batch_no: '',
        barcode: '',
        product_name: '',
        product_code: '',
        category: '',
        company: '',
        color: '',
        size: '',
        packing_type: 'packet',
        sub_packing_type: 'piece',
        items_per_pack: '1',
        unit_of_measure: 'piece',
        cost_price_per_pack: '',
        profit_percent: '',
        sell_price_per_unit: '',
        total_quantity: '',
        quantity_alert: '2',
        expiry_date: '',
        cost_per_unit: '',
        sell_per_pack: '',
        bquantity: '',
        purchase_type: 'packing',
        themeColor: localStorage.getItem('ourzest_theme') || '#6366f1',
        batch_no_manual: false,
        barcode_manual: false,
        existing_batch_nos: []
    });

    const [entryMode, setEntryMode] = useState('single');
    const [bulkFormData, setBulkFormData] = useState(createInitialBulkForm);
    const [bulkRows, setBulkRows] = useState([]);
    const [bulkErrors, setBulkErrors] = useState({});
    const [bulkCategorySearchTerm, setBulkCategorySearchTerm] = useState('');
    const [showBulkCategoryDropdown, setShowBulkCategoryDropdown] = useState(false);
    const [bulkPage, setBulkPage] = useState(0);
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [translations, setTranslations] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    
    // Manual dropdown states
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [showColorDropdown, setShowColorDropdown] = useState(false);
    const [colorSearchTerm, setColorSearchTerm] = useState('');
    const [showSizeDropdown, setShowSizeDropdown] = useState(false);
    const [sizeSearchTerm, setSizeSearchTerm] = useState('');
    
    // State for calculated display values
    const [calculatedCostPerUnit, setCalculatedCostPerUnit] = useState('');
    const [calculatedSellPerPack, setCalculatedSellPerPack] = useState('');
    const [calculatedBQuantity, setCalculatedBQuantity] = useState('');

    // ==============================================
    // BARCODE SCANNER STATE
    // ==============================================
    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);
    // Authoritative lock: once populated by a scan/manual barcode entry,
    // product-name effects and submit-time generation must never replace it.
    const scannedBarcodeRef = useRef('');
    const isScanningRef = useRef(false);
    const bulkCellRefs = useRef({});
    const bulkRowsRef = useRef([]);
    const pendingBulkFocusRef = useRef(null);

    // Refs for dropdowns
    const categoryDropdownRef = useRef(null);
    const bulkCategoryDropdownRef = useRef(null);
    const colorDropdownRef = useRef(null);
    const sizeDropdownRef = useRef(null);
    const draftsDropdownRef = useRef(null);

    // Refs for preventing duplicate submissions
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const lastSubmitTimeRef = useRef(0);
    const lastProductDataRef = useRef('');
    const isInitialLoadRef = useRef(true);
    const isClearingRef = useRef(false);

    // ==============================================
    // CATEGORY, COLOR, SIZE OPTIONS
    // ==============================================
    const categoryOptions = [
        { value: '', label: 'Select Category' },
        { value: 'air_freshner', label: 'AIR FRESHNER' },
        { value: 'hand_wash', label: 'HAND WASH' },
        { value: 'body_wash', label: 'BODY WASH' },
        { value: 'hair_oil', label: 'HAIR OIL' },
        { value: 'shampoo', label: 'SHAMPOO' },
        { value: 'body_scrub', label: 'body scrub' },
        { value: 'hair_serum', label: 'HAIR SERUM' },
        { value: 'cream', label: 'cream' },
        { value: 'soft_cream', label: 'soft cream' },
        { value: 'conditioner', label: 'CONDITIONER' },
        { value: 'aster_shave', label: 'ASTER SHAVE' },
        { value: 'powder', label: 'powder' },
        { value: 'fusion_gel', label: 'fusion gel' },
        { value: 'men_shaving_foam', label: 'men shaving foam' },
        { value: 'shaving_gel', label: 'shaving gel' },
        { value: 'shaving_foam', label: 'SHAVING FOAM' },
        { value: 'hair_colour', label: 'HAIR COLOUR' },
        { value: 'speedy_hair_colour', label: 'speedy hair colour' },
        { value: 'hair_remover', label: 'HAIR REMOVER' },
        { value: 'micellar_water', label: 'micellar water' },
        { value: 'cleansing_milk', label: 'cleansing milk' },
        { value: 'mahandi', label: 'mahandi' },
        { value: 'face_wash', label: 'face wash' },
        { value: 'face_wash_pump', label: 'face wash pump' },
        { value: 'face_wash_tube', label: 'face wash tube' },
        { value: 'body_oil', label: 'body oil' },
        { value: 'men_wash', label: 'men wash' },
        { value: 'loction', label: 'LOCTION' },
        { value: 'why_beauty_face_wash', label: 'why beauty face wash' },
        { value: 'natural_cream', label: 'natural cream' },
        { value: 'fruit_lotion', label: 'fruit lotion' },
        { value: 'baby_lotion', label: 'baby lotion' },
        { value: 'top_to_wash', label: 'top to wash' },
        { value: 'face_scrub', label: 'face scrub' },
        { value: 'flawless', label: 'flawless' },
        { value: 'sparkling', label: 'sparkling' },
        { value: 'body_spray', label: 'BODY SPRAY' },
        { value: 'rollon', label: 'rollon' },
        { value: 'stick', label: 'STICK' },
        { value: 'agemiracle_serum', label: 'agemiracle serum' },
        { value: 'agemiracle_cream', label: 'agemiracle cream' },
        { value: 'hydra_miracle', label: 'hydra miracle' },
        { value: 'bright_miracle', label: 'bright miracle' },
        { value: 'fogg_daba', label: 'fogg daba' },
        { value: 'lotion_red', label: 'lotion red' },
        { value: 'mooth_wash', label: 'MOOTH WASH' },
        { value: 'diamond', label: 'diamond' },
        { value: 'toth_paste', label: 'TOTH PASTE' },
        { value: 'kids', label: 'kids' },
        { value: 'brush', label: 'Brush' },
        { value: 'toth_brush', label: 'TOTH brush' },
        { value: 'amla_hair_oil', label: 'amla hair oil' },
        { value: 'baby_shampo', label: 'baby shampo' },
        { value: 'baby_wipes', label: 'baby wipes' },
        { value: 'men_cream', label: 'men cream' },
        { value: 'sun_block', label: 'SUN BLOCK' },
        { value: 'perfume', label: 'PERFUME' },
        { value: 'pure_coconut_oil', label: 'PURE COCONUT OIL' },
        { value: 'olive_oil', label: 'OLIVE OIL' },
        { value: 'pomace_oil', label: 'POMACE OIL' },
        { value: 'ultra_marine', label: 'ULTRA MARINE' },
        { value: 'gold', label: 'GOLD' },
        { value: 'keratin_shampo', label: 'keratin shampo' },
        { value: 'baby_bath', label: 'baby bath' },
        { value: 'baby_oil', label: 'baby oil' },
        { value: 'baby_powder', label: 'baby powder' },
        { value: 'baby_soap', label: 'baby soap' },
        { value: 'feader', label: 'FEADER' },
        { value: 'pacifiers', label: 'PACIFIERS' },
        { value: 'threple', label: 'threple' },
        { value: 'age_miracle', label: 'age miracle' },
        { value: 'fun_time_orgasm_gel', label: 'FUN TIME ORGASM GEL' },
        { value: 'play_tingle_lube', label: 'PLAY TINGLE LUBE' },
        { value: 'messsege_lube', label: 'MESSEGE LUBE' },
        { value: 'colour_safe', label: 'COLOUR SAFE' },
        { value: 'hair_vitaman_capsol', label: 'HAIR VITAMAN CAPSOL' },
        { value: 'fine_flawless_face_wash', label: 'FINE FLAWLESS FACE WASH' },
        { value: 'trigger', label: 'TRIGGER' },
        { value: 'trigger_crease', label: 'TRIGGER CREASE' },
        { value: 'trigger_power_spray', label: 'TRIGGER POWER SPRAY' },
        { value: 'trigger_febrace_freshner', label: 'TRIGGER FEBRACE FRESHNER' },
        { value: 'trigger_foam_toilet_cleanser', label: 'TRIGGER FOAM TOILET CLEANSER' },
        { value: 'pink_stuff_cleaning_past', label: 'PINK STUFF CLEANING PAST' },
        { value: 'pink_stuff_toilet_cleaner', label: 'PINK STUFF TOILET CLEANER' },
        { value: 'bathroom_foam', label: 'BATHROOM FOAM' },
        { value: 'extanded_bleach', label: 'EXTANDED BLEACH' },
        { value: 'antibacterial_trigger', label: 'ANTIBACTERIAL TRIGGER' },
        { value: 'professional_se_mask', label: 'PROFESSIONAL SE MASK' },
        { value: 'cloths_colour_shape', label: 'CLOTHS COLOUR SHAPE' },
        { value: 'colour', label: 'COLOUR' },
        { value: 'liquid_mask', label: 'LIQUID MASK' },
        { value: 'thin_feel_12_condoms', label: 'THIN FEEL 12 CONDOMS' },
        { value: 'extra_safe_12_condoms', label: 'EXTRA SAFE 12 CONDOMS' },
        { value: 'extended_pleasure_12_condoms', label: 'EXTENDED PLEASURE 12 CONDOMS' },
        { value: 'pleasure_ring_2', label: 'PLEASURE RING 2 PLEASURE RING' },
        { value: 'invisible_12_condoms', label: 'INVISIBLE 12 CONDOMS' },
        { value: 'mutual_pleasure_10_condoms', label: 'MUTUAL PLEASURE 10 CONDOMS' },
        { value: 'thin_feel_3_condoms', label: 'THIN FEEL 3 CONDOMS' },
        { value: 'invisible_3_condoms', label: 'INVISIBLE 3 CONDOMS' },
        { value: 'mutual_pleasure_3_condoms', label: 'MUTUAL PLEASURE 3 CONDOMS' },
        { value: 'extended_pleasure_3_condoms', label: 'EXTENDED PLEASURE 3 CONDOMS' },
        { value: 'feels_x_condoms', label: 'FEELS X CONDOMS 3 CONDOMS' },
        { value: 'pink_stuff_miracle_cleaner_cream', label: 'PINK STUFF MIRACLE CLEANER CREAM' },
        { value: 'milk_lotion', label: 'MILK LOTION' },
        { value: 'serum', label: 'SERUM' },
        { value: 'moov_wax_spray', label: 'MOOV WAX SPRAY' },
        { value: 'wax_spray', label: 'WAX SPRAY' },
        { value: 'vinegar_hair_colour', label: 'VINEGAR HAIR COLOUR' },
        { value: 'hydro_boost_cream', label: 'HYDRO BOOST CREAM' },
        { value: 'soap', label: 'SOAP' },
        { value: 'pump_shampoo', label: 'PUMP SHAMPOO' },
        { value: 'rice_face_wash', label: 'RICE FACE WASH' },
        { value: 'pump_colour', label: 'PUMP COLOUR' },
        { value: 'capsule_box', label: 'CAPSULE BOX' },
        { value: 'oil', label: 'OIL' },
        { value: 'gift_pack', label: 'gift pack' },
        { value: 'colgate_max_fresh', label: 'COLGATE MAX FRESH' },
        { value: 'hair_brush', label: 'HAIR BRUSH' },
        { value: 'hair_straightener', label: 'HAIR STRAIGHTENER' },
        { value: 'hair_trimmer', label: 'HAIR TRIMMER' },
        { value: 'hair_clipper_machine', label: 'HAIR CLIPPER MACHINE' },
        { value: 'hair_dryer', label: 'HAIR DRYER' },
        { value: 'multi_function_grooming_kit', label: 'MULTI FUNCTION GROOMING KIT' },
        { value: 'professional_mens_trimmer', label: 'PROFESSIONAL MENS TRIMMER' },
        { value: 'professional_hair_trimmer', label: 'PROFESSIONAL HAIR TRIMMER' },
        { value: 'professional_hair_clipper', label: 'PROFESSIONAL HAIR CLIPPER' },
        { value: 'fascial_gun', label: 'FASCIAL GUN' },
        { value: 'mini_messege_gun', label: 'MINI MESSEGE GUN' },
        { value: 'professional_3in1_grooming_kit', label: 'PROFESSIONAL 3IN1 GROOMING KIT' },
        { value: 'beauty_kit_5in1', label: '5 IN 1 MULTIFUNTIONAL BEAUTY KIT' },
        { value: 'professional_foil_shaver', label: 'PROFESSIONAL FOIL SHAVER' },
        { value: 'rechargable_nose_trimmer', label: 'RECHARGABLE NOSE TRIMMER' },
        { value: 'professional_ladys_epilator_set', label: 'PROFESSIONAL LADYS EPILATOR SET' },
        { value: 'professional_mens_shaver', label: 'PROFESSIONAL MENS SHAVER' },
        { value: 'beauty_kit_3in1', label: '3IN1 MULTIFUNCTIONAL BEAUTY KIT' },
        { value: 'pore_cleaning_apparatus', label: 'PORE CLEANING APPARATUS' },
        { value: 'professional_lady_epilator', label: 'PROFESSIONAL LADY EPILATOR' },
        { value: 'professional_corded_clipper', label: 'PROFESSIONAL CORDED CLIPPER' },
        { value: 'rechargable_shaver_hair_clipper_nose_trimmer', label: 'RECHARGABLE SHAVER HAIR CLIPPER NOSE TRIMMER' },
        { value: 'shaver_suit_4in1_ladies', label: '4IN1 SHAVER SUIT FOR LADIES' },
        { value: 'washable_shaver', label: 'WASHABLE SHAVER' },
        { value: 'rechargable_shover', label: 'RECHAGABLE SHOVER' },
        { value: 'hair_removal_wax_machine', label: 'HAIR REMOVAL WAX MACHINE' },
        { value: 'fascia_gun_masaj', label: 'FASCIA GUN MASAJ' },
        { value: 'pro_wax_machine', label: 'PRO WAX MACHINE' },
        { value: 'limited_edition_body_face_set', label: 'LIMITED EDITION FOR BODY FACE SET' },
        { value: 'rechargable_hair_remover', label: 'RECHARGABLE HAIR REMOVER' },
        { value: 'professional_4in1_ladys_epilator_kit', label: 'PROFESSIONAL 4IN1 LADYS EPILATOR KIT' },
        { value: 'mens_grooming_kit', label: 'MENS GROOMING KIT' },
        { value: 'shave_body_hair_implement', label: 'SHAVE BODY HAIR IMPLEMENT' },
        { value: 'electric_face_cleansing_brush', label: 'ELECTRIC FACE CLEANSING BRUSH' },
        { value: 'cordless_shaver', label: 'CORDLESS SHAVER' },
        { value: 'professional_clipper', label: 'PROFESSIONAL CLIPPER' },
        { value: 'camping_light', label: 'CAMPING LIGHT' },
        { value: 'solar_charging_camping_light', label: 'SOLAR CHARGING CAMPING LIGHT' },
        { value: 'large_flood_lighting', label: 'LARGE FLOOD LIGHTING' },
        { value: 'lipossible_wax', label: 'LIPOSSIBLE WAX' },
        { value: 'face_serum', label: 'FACE SERUM' },
        { value: 'pump_face_wash', label: 'PUMP FACE WASH' },
        { value: 'shaving_gell', label: 'SHAVING GELL' },
        { value: 'after_shave', label: 'AFTER SHAVE' },
        { value: 'whitening_cream', label: 'WHITENING CREAM' },
        { value: 'growth_damage_oil', label: 'GROWTH DAMAGE OIL' },
        { value: 'kids_shampoo', label: 'KIDS SHAMPOO' },
        { value: 'jelly', label: 'JELLY' },
        { value: 'lip_stick', label: 'LIP STICK' },
        { value: 'hair_roller', label: 'HAIR ROLLER' },
        { value: 'hair_comb', label: 'HAIR COMB' },
        { value: 'carbon_antistatic_comb', label: 'CARBON ANTISTATIC COMB' },
        { value: 'pure_soft_radiant_comb', label: 'PURE.SOFT RADIANT COMB' },
        { value: 'nail_cutter', label: 'NAIL CUTTER' },
        { value: 'kid_manicure_set', label: 'KID MANICURE SET0' },
        { value: 'makeup_brush_jb06', label: 'MAKEUP BRUSH JB06' },
        { value: 'makeup_brush_jb20l', label: 'MAKEUP BRUSH JB20L' },
        { value: 'makeup_brush_jb28j', label: 'MAKEUP BRUSH JB28J' },
        { value: 'makeup_brush_jb20w', label: 'MAKEUP BRUSH JB20W' },
        { value: 'makeup_brush_jb03l', label: 'MAKEUP BRUSH JB03L' },
        { value: 'makeup_puff', label: 'MAKEUP PUFF' },
        { value: 'tweezer', label: 'TWEEZER' },
        { value: 'everyday_essential_5in1_pro_set', label: 'EVERYDAY ESSENTIAL 5IN1 PRO SET' },
        { value: 'ance_needle', label: 'ANCE NEEDLE' },
        { value: 'dhaga', label: 'DHAGA' },
        { value: 'head_band_hair_clip_beauty', label: 'HEAD BAND HAIR CLIP BEAUTY' },
        { value: 'cotton_wax_strip', label: 'COTTON WAX STRIP' },
        { value: 'foot_file', label: 'FOOT FILE' },
        { value: 'double_sided_foot_file', label: 'DOUBLE-SIDED FOOT FILE' },
        { value: 'eyelash_curler', label: 'EYELASH CURLER' },
        { value: 'cosmetic_mirror', label: 'COSMETIC MIRROR' },
        { value: 'face_wash_puff_2pcs_set', label: 'FACE WASH PUFF-2PCS SET' },
        { value: 'eyebrow_razor', label: 'EYEBROW RAZOR' },
        { value: 'bath_brush', label: 'BATH BRUSH' },
        { value: 'professional_foundation_sponge', label: 'PROFESSIONAL FOUNDATION SPONGE' },
        { value: 'hair_remover_wax', label: 'HAIR REMOVER WAX' },
        { value: 'razor_3_for_women', label: '3 RAZOR FOR WOMEN' },
        { value: 'natural_cotton_ball', label: 'NATURAL COTTON BALL' },
        { value: 'eyebrown_razors_set', label: 'EYEBROWN RAZORS SET' },
        { value: 'face_wash_puff', label: 'FACE WASH PUFF' },
        { value: 'hair_remowing_wax_roll', label: 'HAIR REMOWING WAX ROLL' },
        { value: 'eyebrow_sissors', label: 'EYEBROW SISSORS' },
        { value: 'retro_lamp', label: 'RETRO LAMP' },
        { value: 'soothing_gell', label: 'SOOTHING GELL' },
        { value: 'skin_active', label: 'SKIN ACTIVE' },
        { value: 'hyaluronic_gell_wash', label: 'HYALURONIC GELL WASH' },
        { value: 'sakura_glow', label: 'SAKURA GLOW' },
        { value: 'mint_mouth_spray', label: 'MINT MOUTH SPRAY' },
        { value: 'eye_makeup_remover_2in1', label: '2IN1 EYE MAKEUP REMOVER' },
        { value: 'age_perfect_toner', label: 'AGE PERFECT TONER' },
        { value: 'hair_gell', label: 'HAIR GELL' },
        { value: 'lip_bam', label: 'LIP BAM' },
        { value: 'softden_razor', label: 'SOFTDEN RAZOR' },
        { value: 'blade_2_gripmix', label: 'BLADE 2 GRIPMIX' },
        { value: 'body_mist', label: 'BODY MIST' },
        { value: 'instant_glowing_serum', label: 'INSTANT GLOWING SERUM' },
        { value: 'hyaluron_serum', label: 'HYALURON SERUM' },
        { value: 'seoul_1988_eye_cream', label: 'SEOUL 1988 EYE CREAM' },
        { value: 'seoul_1988_serum', label: 'SEOUL 1988 SERUM' },
        { value: 'daily_moisturizing_lotion', label: 'DAILY MOITURIZING LOTION' },
        { value: 'foaming_facial_cleanser', label: 'FOAMING FACIAL CLEANSER NORMAL OILY SKIN' },
        { value: 'cleanser_cream', label: 'CLEANSER CREAM' },
        { value: 'collagen_jelly_cream', label: 'COLLAGEN JELLY CREAM' },
        { value: 'deep_vita_c_capsule_cream', label: 'DEEP VITA C CAPSULE CREAM' },
        { value: 'pdrn_pink_collagen_capsule_cream', label: 'PDRN PINK COLLAGEN CAPSULE CREAM' },
        { value: 'hyaluronic_acid_intensive_cream', label: 'HYALURONIC ACID INTENSIVE CREAM' },
        { value: 'azelaic_serum', label: 'AZELAIC SERUM' },
        { value: 'niacinamide_serum', label: 'NIACINAMIDE SERUM' },
        { value: 'txa_niacinamide_capsule_cream', label: 'TXA NIACINAMIDE CAPSULE CREAM' },
        { value: 'brightning_care_sunscreen', label: 'BRIGHTNING CARE SUNSCREEN' },
        { value: 'brightning_sunblock_cream', label: 'BRIGHTNING SUNBLOCK CREAM' },
        { value: 'booster_serum', label: 'BOOSTER SERUM' },
        { value: 'salicylic_acid_daily_gentle_cleanser', label: 'SALICYLIC ACID DAILY GENTLE CLEANSER' },
        { value: 'tone_brightning_capsule_cream', label: 'TONE BRIGHTNING CAPSULE CREAM' },
        { value: 'soothing_cream', label: 'SOOTHING CREAM' },
        { value: 'tone_brightning_cleansing_face_wash', label: 'TONE BRIGHTNING CLEASING FACE WASH' },
        { value: 'skin_serum', label: 'SKIN SERUM' },
        { value: 'skin_booster_serum', label: 'SKIN BOOSTER SERUM' },
        { value: 'azelaic_acid_suspension_10', label: 'AZELAIC ACID SUSPENSION 10 SKIN SERUM' },
        { value: 'vitaman_c_skin_serum', label: 'VITAMAN C SKIN SERUM' },
        { value: 'glycolic_acid_7_exfoliating_toner', label: 'GLYCOLIC ACID 7 EXFOLIATING TONER' },
        { value: 'hyalu_cica_water_fit_sun_serum', label: 'HYALU CICA WATER FIT SUN SERUM' },
        { value: 'retinol_shot_tightning_serum', label: 'RETINOL SHOT TIGHTNING SERUM' },
        { value: 'collagen_firming_sun_cream', label: 'COLLAGEN FIRMING SUN CREAM' },
        { value: 'aloe_aqua_tone_up_sunscreen', label: 'ALOE 54.2 AQUA TONE UP SUNSCREEN' },
        { value: 'relief_sun_rise_sunblock', label: 'RELIEF SUN RISE SUNBLOCK' },
        { value: 'professional_silk_touch_milk_cream', label: 'PROFESSIONAL SILK TOUCH MILK CREAM' },
        { value: 'moisturizing_cream', label: 'MOISTURIZING CREAM' },
        { value: 'light_cleansing_oil', label: 'LIGHT CLEANSING OIL' },
        { value: 'power_grip_primer', label: 'POWER GRIP PRIMER' },
        { value: 'tone_brightning_boosting_toner', label: 'TONE BRIGHTNING BOOSTING TONER' },
        { value: 'retinol_serum', label: 'RETINOL SERUM' },
        { value: 'vitamin_c_serum', label: 'VITAMIN C SERUM' },
        { value: 'infaillible_3_second_setting_mist', label: 'INFAILLIBLE 3 SECOND SETTING MIST' },
        { value: 'muskara', label: 'MUSKARA' },
        { value: 'foundation_makeup', label: 'FOUNDATION MAKEUP' },
        { value: 'relief_cream_345', label: '345 RELIEF CREAM' },
        { value: 'revive_eye_serum', label: 'REVIVE EYE SERUM' },
        { value: 'dark_spot_correcting_glow_serum', label: 'DARK SPOT CORRECTING GLOW SERUM' },
        { value: 'gentle_hydrating_shampoo', label: 'GENTLE HYDRATING SHAMPOO' },
        { value: 'jelly_primer', label: 'JELLY PRIMER' },
        { value: 'foundation_tube_makeup', label: 'FOUNDATION TUBE MAKEUP' },
        { value: 'makeup_24h_visibly_primer', label: 'MAKEUP FOR 24H VISIBLY PRIMER SKIN IN 3 WEEKS' },
        { value: 'retinol_shot_tightning_serum_alt', label: 'RETINOL SHOT TIGHTNING SERUM' },
        { value: 'true_match_foundation_makeup', label: 'TRUE MATCH FOUNDATION MAKEUP' },
        { value: 'infaillable_foundation_makeup', label: 'INFAILLABLE FOUNDATION MAKEUP' },
        { value: 'formezing_serum', label: 'FORMEZING SERUM' },
        { value: 'tea_trica_relief_ampoule_serum', label: 'TEA TRICA RELIEF AMPOULE SERUM' },
        { value: 'probia_cica_intensive_ampoule_serum', label: 'PROBIA CICA INTENSIVE AMPOULE SERUM' },
        { value: 'brightning_serum', label: 'BRIGHTNING SERUM' },
        { value: 'direct_acids', label: 'DIRECT ACIDS' },
        { value: 'niacinamide_10_serum', label: 'NIACINAMIDE 10 SERUM' },
        { value: 'puch', label: 'PUCH' },
        { value: 'pedicure_stone', label: 'PEDICURE STONE' },
        { value: 'cleanser_foam', label: 'CLEANSER FOAM' }
    ];

    const colorOptions = [
        { value: '', label: 'Select Color' },
        { value: 'red', label: 'Red' },
        { value: 'blue', label: 'Blue' },
        { value: 'green', label: 'Green' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'pink', label: 'Pink' },
        { value: 'brown', label: 'Brown' },
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'gray', label: 'Gray' },
        { value: 'silver', label: 'Silver' },
        { value: 'gold', label: 'Gold' },
        { value: 'beige', label: 'Beige' },
        { value: 'cream', label: 'Cream' },
        { value: 'maroon', label: 'Maroon' },
        { value: 'navy', label: 'Navy Blue' },
        { value: 'teal', label: 'Teal' },
        { value: 'turquoise', label: 'Turquoise' },
        { value: 'lime', label: 'Lime' },
        { value: 'olive', label: 'Olive' },
        { value: 'coral', label: 'Coral' },
        { value: 'salmon', label: 'Salmon' },
        { value: 'lavender', label: 'Lavender' },
        { value: 'violet', label: 'Violet' },
        { value: 'magenta', label: 'Magenta' },
        { value: 'cyan', label: 'Cyan' },
        { value: 'indigo', label: 'Indigo' },
        { value: 'amber', label: 'Amber' },
        { value: 'peach', label: 'Peach' },
        { value: 'mint', label: 'Mint' },
        { value: 'sage', label: 'Sage' },
        { value: 'tan', label: 'Tan' },
        { value: 'ivory', label: 'Ivory' },
        { value: 'charcoal', label: 'Charcoal' },
        { value: 'slate', label: 'Slate' },
        { value: 'champagne', label: 'Champagne' },
        { value: 'rose', label: 'Rose' },
        { value: 'crimson', label: 'Crimson' },
        { value: 'burgundy', label: 'Burgundy' },
        { value: 'plum', label: 'Plum' },
        { value: 'mauve', label: 'Mauve' },
        { value: 'fawn', label: 'Fawn' },
        { value: 'mustard', label: 'Mustard' },
        { value: 'rust', label: 'Rust' },
        { value: 'terracotta', label: 'Terracotta' },
        { value: 'copper', label: 'Copper' },
        { value: 'bronze', label: 'Bronze' },
        { value: 'metallic', label: 'Metallic' },
        { value: 'multi', label: 'Multi-Color' },
        { value: 'transparent', label: 'Transparent' },
        { value: 'other', label: 'Other' }
    ];

    const sizeOptions = [
        { value: '', label: 'Select Size' },
        { value: 's', label: 'S' },
        { value: 'm', label: 'M' },
        { value: 'l', label: 'L' },
        { value: 'xl', label: 'XL' },
        { value: 'xxl', label: 'XXL' },
        { value: 'xxxl', label: 'XXXL' },
        { value: '4xl', label: '4XL' },
        { value: '5xl', label: '5XL' },
        { value: '6xl', label: '6XL' },
        { value: '7xl', label: '7XL' },
        { value: '8xl', label: '8XL' },
        { value: '0', label: '0' },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' },
        { value: '7', label: '7' },
        { value: '8', label: '8' },
        { value: '9', label: '9' },
        { value: '10', label: '10' },
        { value: '11', label: '11' },
        { value: '12', label: '12' },
        { value: '13', label: '13' },
        { value: '14', label: '14' },
        { value: '1_cm', label: '1 cm' },
        { value: '2_cm', label: '2 cm' },
        { value: '3_cm', label: '3 cm' },
        { value: '4_cm', label: '4 cm' },
        { value: '5_cm', label: '5 cm' },
        { value: '6_cm', label: '6 cm' },
        { value: '7_cm', label: '7 cm' },
        { value: '8_cm', label: '8 cm' },
        { value: '9_cm', label: '9 cm' },
        { value: '10_cm', label: '10 cm' },
        { value: '11_cm', label: '11 cm' },
        { value: '12_cm', label: '12 cm' },
        { value: '13_cm', label: '13 cm' },
        { value: '14_cm', label: '14 cm' },
        { value: '15_cm', label: '15 cm' },
        { value: '16_cm', label: '16 cm' },
        { value: '17_cm', label: '17 cm' },
        { value: '18_cm', label: '18 cm' },
        { value: '19_cm', label: '19 cm' },
        { value: '20_cm', label: '20 cm' },
        { value: '22_cm', label: '22 cm' },
        { value: '24_cm', label: '24 cm' },
        { value: '25_cm', label: '25 cm' },
        { value: '26_cm', label: '26 cm' },
        { value: '28_cm', label: '28 cm' },
        { value: '30_cm', label: '30 cm' },
        { value: '32_cm', label: '32 cm' },
        { value: '35_cm', label: '35 cm' },
        { value: '40_cm', label: '40 cm' },
        { value: '45_cm', label: '45 cm' },
        { value: '50_cm', label: '50 cm' },
        { value: '55_cm', label: '55 cm' },
        { value: '60_cm', label: '60 cm' },
        { value: '65_cm', label: '65 cm' },
        { value: '70_cm', label: '70 cm' },
        { value: '75_cm', label: '75 cm' },
        { value: '80_cm', label: '80 cm' },
        { value: '85_cm', label: '85 cm' },
        { value: '90_cm', label: '90 cm' },
        { value: '95_cm', label: '95 cm' },
        { value: '100_cm', label: '100 cm' },
        { value: '1_ft', label: '1 ft' },
        { value: '2_ft', label: '2 ft' },
        { value: '3_ft', label: '3 ft' },
        { value: '4_ft', label: '4 ft' },
        { value: '5_ft', label: '5 ft' },
        { value: '6_ft', label: '6 ft' },
        { value: '7_ft', label: '7 ft' },
        { value: '8_ft', label: '8 ft' },
        { value: '9_ft', label: '9 ft' },
        { value: '10_ft', label: '10 ft' },
        { value: '11_ft', label: '11 ft' },
        { value: '12_ft', label: '12 ft' },
        { value: '13_ft', label: '13 ft' },
        { value: '14_ft', label: '14 ft' },
        { value: '15_ft', label: '15 ft' },
        { value: '16_ft', label: '16 ft' },
        { value: '17_ft', label: '17 ft' },
        { value: '18_ft', label: '18 ft' },
        { value: '19_ft', label: '19 ft' },
        { value: '20_ft', label: '20 ft' },
        { value: '0.5_m', label: '0.5 m' },
        { value: '1_m', label: '1 m' },
        { value: '1.5_m', label: '1.5 m' },
        { value: '2_m', label: '2 m' },
        { value: '2.5_m', label: '2.5 m' },
        { value: '3_m', label: '3 m' },
        { value: '4_m', label: '4 m' },
        { value: '5_m', label: '5 m' },
        { value: '6_m', label: '6 m' },
        { value: '7_m', label: '7 m' },
        { value: '8_m', label: '8 m' },
        { value: '9_m', label: '9 m' },
        { value: '10_m', label: '10 m' },
        { value: '12_m', label: '12 m' },
        { value: '15_m', label: '15 m' },
        { value: '20_m', label: '20 m' },
        { value: '25_m', label: '25 m' },
        { value: '30_m', label: '30 m' },
        { value: '40_m', label: '40 m' },
        { value: '50_m', label: '50 m' },
        { value: '60_m', label: '60 m' },
        { value: '70_m', label: '70 m' },
        { value: '80_m', label: '80 m' },
        { value: '90_m', label: '90 m' },
        { value: '100_m', label: '100 m' },
        { value: '60_gsm', label: '60 GSM' },
        { value: '70_gsm', label: '70 GSM' },
        { value: '80_gsm', label: '80 GSM' },
        { value: '90_gsm', label: '90 GSM' },
        { value: '100_gsm', label: '100 GSM' },
        { value: '120_gsm', label: '120 GSM' },
        { value: '140_gsm', label: '140 GSM' },
        { value: '160_gsm', label: '160 GSM' },
        { value: '180_gsm', label: '180 GSM' },
        { value: '200_gsm', label: '200 GSM' },
        { value: '250_gsm', label: '250 GSM' },
        { value: '300_gsm', label: '300 GSM' },
        { value: '350_gsm', label: '350 GSM' },
        { value: 'one_size', label: 'One Size' },
        { value: 'free_size', label: 'Free Size' },
        { value: 'adjustable', label: 'Adjustable' },
        { value: 'custom', label: 'Custom' },
        { value: 'other', label: 'Other' }
    ];

    // ==============================================
    // HELPER FUNCTIONS
    // ==============================================
    const getUnitNumericValue = (unitOfMeasure) => {
        const unitValues = {
            'piece': 1,
            'dozen': 12,
            'box': 1,
            'packet': 1,
            'can': 1,
            'bottle': 1
        };
        return unitValues[unitOfMeasure] || 1;
    };

    const getUnitDisplayLabel = (unitOfMeasure) => {
        const labels = {
            'piece': 'Piece',
            'dozen': 'Dozen',
            'box': 'Box',
            'packet': 'Packet',
            'can': 'Can',
            'bottle': 'Bottle'
        };
        return labels[unitOfMeasure] || 'Unit';
    };

    const getPluralUnitLabel = (label) => {
        const plurals = {
            'Piece': 'Pieces',
            'Dozen': 'Dozens',
            'Box': 'Boxes',
            'Packet': 'Packets',
            'Can': 'Cans',
            'Bottle': 'Bottles',
            'Unit': 'Units'
        };
        return plurals[label] || label + 's';
    };

    const calculateBQuantityString = (itemsPerPack, unitOfMeasure, totalQuantity) => {
        if (!itemsPerPack || itemsPerPack <= 0 || !totalQuantity || totalQuantity <= 0 || !unitOfMeasure) {
            return '';
        }
        
        const unitValue = getUnitNumericValue(unitOfMeasure);
        const totalItems = itemsPerPack * totalQuantity;
        const bulkValue = totalItems * unitValue;
        const unitLabel = getUnitDisplayLabel(unitOfMeasure);
        
        let formattedValue;
        if (Math.floor(bulkValue) === bulkValue) {
            formattedValue = bulkValue.toFixed(0);
        } else {
            formattedValue = bulkValue.toFixed(2);
        }
        
        const finalLabel = bulkValue !== 1 ? getPluralUnitLabel(unitLabel) : unitLabel;
        
        return `${formattedValue} ${finalLabel}`;
    };

    const calculateCostPerUnitNumeric = (costPerPack, itemsPerPack, unitOfMeasure) => {
        if (!costPerPack || costPerPack <= 0 || !itemsPerPack || itemsPerPack <= 0) {
            return 0;
        }
        const perUnit = costPerPack / itemsPerPack;
        const unitValue = getUnitNumericValue(unitOfMeasure);
        const costPerBaseUnit = perUnit / unitValue;
        return costPerBaseUnit;
    };

    const calculateSellPerPackFromUnit = (sellPricePerUnit, itemsPerPack, unitOfMeasure) => {
        if (!sellPricePerUnit || sellPricePerUnit <= 0 || !itemsPerPack || itemsPerPack <= 0 || !unitOfMeasure) {
            return 0;
        }
        const unitValue = getUnitNumericValue(unitOfMeasure);
        const perUnitPrice = sellPricePerUnit * unitValue;
        const sellPerPack = perUnitPrice * itemsPerPack;
        return sellPerPack;
    };

    const formatPrice = (value) => {
        if (value === 0) return '';
        if (value < 0.01) return value.toFixed(4);
        if (value < 1) return value.toFixed(3);
        return value.toFixed(2);
    };

    // ==============================================
    // FILTERED OPTIONS
    // ==============================================
    const filteredCategories = categorySearchTerm.trim() === '' 
        ? categoryOptions 
        : categoryOptions.filter(cat => 
            cat.label.toLowerCase().includes(categorySearchTerm.toLowerCase())
          );

    const filteredBulkCategories = bulkCategorySearchTerm.trim() === ''
        ? categoryOptions
        : categoryOptions.filter(cat =>
            cat.label.toLowerCase().includes(bulkCategorySearchTerm.toLowerCase())
          );

    const filteredColors = colorSearchTerm.trim() === '' 
        ? colorOptions.filter(c => c.value !== '')
        : colorOptions.filter(c => 
            c.value !== '' && c.label.toLowerCase().includes(colorSearchTerm.toLowerCase())
          );

    const filteredSizes = sizeSearchTerm.trim() === ''
        ? sizeOptions.filter(s => s.value !== '')
        : sizeOptions.filter(s => 
            s.value !== '' && s.label.toLowerCase().includes(sizeSearchTerm.toLowerCase())
          );

    // Keep the number of mounted table rows bounded while allowing any RQ.
    // This is view pagination only; every row remains in bulkRows and is saved.
    const bulkPageCount = Math.max(1, Math.ceil(bulkRows.length / BULK_PAGE_SIZE));
    const safeBulkPage = Math.min(bulkPage, bulkPageCount - 1);
    const bulkPageStart = safeBulkPage * BULK_PAGE_SIZE;
    const visibleBulkRows = useMemo(
        () => bulkRows.slice(bulkPageStart, bulkPageStart + BULK_PAGE_SIZE),
        [bulkRows, bulkPageStart]
    );
    const scannedBulkCount = useMemo(
        () => bulkRows.reduce((count, row) => count + (row.barcode.trim() ? 1 : 0), 0),
        [bulkRows]
    );

    // ==============================================
    // SHOW TOAST FUNCTION
    // ==============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    const focusBulkCell = useCallback((rowIndex, field = 'barcode') => {
        if (!Number.isInteger(rowIndex) || rowIndex < 0) return;

        pendingBulkFocusRef.current = { rowIndex, field };
        setBulkPage(Math.floor(rowIndex / BULK_PAGE_SIZE));

        let attempts = 0;
        const attemptFocus = () => {
            const element = bulkCellRefs.current[`${rowIndex}-${field}`];
            if (element) {
                element.focus();
                if (field === 'barcode' && element.select) element.select();
                pendingBulkFocusRef.current = null;
            } else if (pendingBulkFocusRef.current && attempts < 12) {
                attempts += 1;
                requestAnimationFrame(attemptFocus);
            } else {
                pendingBulkFocusRef.current = null;
            }
        };
        requestAnimationFrame(attemptFocus);
    }, []);

    // ==============================================
    // HANDLE BARCODE SCAN - Defined AFTER all state declarations
    // ==============================================
    const handleBarcodeScan = useCallback(async (barcode) => {
        // Prevent duplicate scans
        if (isScanningRef.current) return;
        if (!barcode || barcode.trim() === '') return;

        const scannedBarcode = barcode.trim();

        if (entryMode === 'bulk' && !isEditMode) {
            const currentRows = bulkRowsRef.current;
            if (currentRows.length === 0) {
                showToast('warning', 'Enter RQ first so the barcode rows can be created.');
                return;
            }

            // A user-focused barcode cell always wins. Without a focused
            // barcode cell, use the first empty row from the top. This avoids
            // competing pointer/focus counters that previously skipped or
            // swapped rows during consecutive scans.
            const activeElement = document.activeElement;
            const focusedIndexValue = activeElement?.getAttribute?.('data-bulk-barcode-index');
            const focusedIndex = focusedIndexValue !== null && focusedIndexValue !== undefined
                ? Number(focusedIndexValue)
                : -1;
            const hasFocusedBarcode = Number.isInteger(focusedIndex) &&
                focusedIndex >= 0 && focusedIndex < currentRows.length;
            const targetIndex = hasFocusedBarcode
                ? focusedIndex
                : currentRows.findIndex(row => !row.barcode.trim());

            if (targetIndex === -1) {
                showToast('warning', 'All bulk barcode rows are already filled.');
                return;
            }

            const duplicateIndex = currentRows.findIndex((row, index) =>
                index !== targetIndex &&
                row.barcode.trim().toUpperCase() === scannedBarcode.toUpperCase()
            );
            if (duplicateIndex !== -1) {
                showToast('warning', `Barcode already scanned in row ${duplicateIndex + 1}.`);
                focusBulkCell(duplicateIndex, 'barcode');
                return;
            }

            const nextRows = currentRows.slice();
            nextRows[targetIndex] = { ...nextRows[targetIndex], barcode: scannedBarcode };
            bulkRowsRef.current = nextRows;
            setBulkRows(nextRows);
            setLastScannedBarcode(scannedBarcode);
            setBulkErrors(prev => ({ ...prev, rows: '', [`barcode_${targetIndex}`]: '' }));
            showToast('success', `Barcode placed in row ${targetIndex + 1}.`);

            const nextEmptyAfterTarget = nextRows.findIndex((row, index) =>
                index > targetIndex && !row.barcode.trim()
            );
            const nextEmptyIndex = nextEmptyAfterTarget !== -1
                ? nextEmptyAfterTarget
                : nextRows.findIndex(row => !row.barcode.trim());
            if (nextEmptyIndex !== -1) focusBulkCell(nextEmptyIndex, 'barcode');
            return;
        }

        scannedBarcodeRef.current = scannedBarcode;
        setLastScannedBarcode(scannedBarcode);

        // Put the scan into the visible barcode field immediately. Database
        // lookup happens afterwards, so a slow/offline request cannot lose it.
        if (!isEditMode) {
            setFormData(prev => ({
                ...prev,
                barcode: scannedBarcode,
                barcode_manual: true
            }));
        }

        if (!currentShop?.shop_id) {
            showToast('warning', translations.selectShop || 'Please select a shop first');
            return;
        }

        isScanningRef.current = true;

        try {
            // First, check if this barcode matches an existing product
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record_by_barcode',
                    barcode: scannedBarcode,
                    username: user?.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success' && data.data) {
                const product = data.data;
                scannedBarcodeRef.current = product.barcode || scannedBarcode;
                
                if (isEditMode && formData.product_name) {
                    if (!window.confirm('Loading this product will replace current form data. Continue?')) {
                        isScanningRef.current = false;
                        return;
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    record_id: product.id,
                    date: product.date || new Date().toISOString().split('T')[0],
                    batch_no: product.batch_no || '',
                    barcode: product.barcode || '',
                    product_name: product.product_name || '',
                    product_code: product.product_code || '',
                    category: product.category || '',
                    company: product.company || '',
                    color: product.color || '',
                    size: product.size || '',
                    packing_type: product.packing_type || 'packet',
                    sub_packing_type: product.sub_packing_type || 'piece',
                    items_per_pack: product.items_per_pack || '1',
                    unit_of_measure: product.unit_of_measure || 'piece',
                    cost_price_per_pack: product.cost_price_per_pack || product.cost_per_pack || '',
                    profit_percent: getStoredOrCalculatedProfitPercent(product),
                    sell_price_per_unit: product.sell_price_per_unit || '',
                    total_quantity: product.total_quantity || product.quantity || '',
                    quantity_alert: product.quantity_alert || '2',
                    expiry_date: product.expiry_date || '',
                    cost_per_unit: product.cost_per_unit || '',
                    sell_per_pack: product.sell_per_pack || '',
                    bquantity: product.bquantity || '',
                    batch_no_manual: true,
                    barcode_manual: true,
                    existing_batch_nos: prev.existing_batch_nos || []
                }));

                const selectedCategory = categoryOptions.find(c => c.value === product.category);
                if (selectedCategory) setCategorySearchTerm(selectedCategory.label);
                
                const selectedColor = colorOptions.find(c => c.value === product.color);
                if (selectedColor) setColorSearchTerm(selectedColor.label);
                
                const selectedSize = sizeOptions.find(s => s.value === product.size);
                if (selectedSize) setSizeSearchTerm(selectedSize.label);

                setIsEditMode(true);
                
                if (product.cost_per_unit) setCalculatedCostPerUnit(product.cost_per_unit);
                if (product.sell_per_pack) setCalculatedSellPerPack(product.sell_per_pack);
                if (product.bquantity) setCalculatedBQuantity(product.bquantity);

                showToast('success', `${translations.productFound || 'Product found!'} ${product.product_name}`);

            } else if (!isEditMode) {
                // A physical scan is always intentional. It may replace an
                // auto-generated placeholder, and must not be regenerated later.
                setFormData(prev => ({
                    ...prev,
                    barcode: scannedBarcode,
                    barcode_manual: true
                }));
                showToast('info', `${translations.barcodeSet || 'Barcode set'}: ${scannedBarcode}`);

                setTimeout(() => {
                    if (productNameRef.current) productNameRef.current.focus();
                }, 300);
            } else {
                showToast('warning', `${translations.productNotFound || 'Product not found'}: ${scannedBarcode}`);
            }
        } catch (error) {
            console.error('Barcode scan error:', error);
            showToast('error', translations.networkError || 'Error processing barcode scan');
        } finally {
            isScanningRef.current = false;
        }
    }, [currentShop, user?.username, isEditMode, entryMode, formData.barcode, formData.product_name, translations, showToast, focusBulkCell]);

    // ==============================================
    // INITIALIZE BARCODE SCANNER
    // ==============================================
    useBarcodeScanner(handleBarcodeScan, isScannerActive);

    // ==============================================
    // EFFECTS
    // ==============================================
    
    // Click outside handlers for dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
            if (bulkCategoryDropdownRef.current && !bulkCategoryDropdownRef.current.contains(event.target)) {
                setShowBulkCategoryDropdown(false);
            }
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
            if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target)) {
                setShowColorDropdown(false);
            }
            if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target)) {
                setShowSizeDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Tab key handler
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowCategoryDropdown(false);
                setShowColorDropdown(false);
                setShowSizeDropdown(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // Auto-focus
    useEffect(() => {
        setTimeout(() => {
            if (productNameRef.current) {
                productNameRef.current.focus();
            }
        }, 100);
    }, []);

    // Calculate fields
    useEffect(() => {
        let newCostPerUnit = '';
        let newSellPerPack = '';
        let newBQuantity = '';
        
        if (formData.cost_price_per_pack && formData.items_per_pack && formData.unit_of_measure) {
            const cpuNumeric = calculateCostPerUnitNumeric(
                parseFloat(formData.cost_price_per_pack),
                parseFloat(formData.items_per_pack),
                formData.unit_of_measure
            );
            newCostPerUnit = formatPrice(cpuNumeric);
            setCalculatedCostPerUnit(newCostPerUnit);
        } else {
            setCalculatedCostPerUnit('');
        }
        
        if (formData.sell_price_per_unit && formData.items_per_pack && formData.unit_of_measure) {
            const spPackNumeric = calculateSellPerPackFromUnit(
                parseFloat(formData.sell_price_per_unit),
                parseFloat(formData.items_per_pack),
                formData.unit_of_measure
            );
            newSellPerPack = formatPrice(spPackNumeric);
            setCalculatedSellPerPack(newSellPerPack);
        } else {
            setCalculatedSellPerPack('');
        }
        
        if (formData.items_per_pack && formData.unit_of_measure && formData.total_quantity) {
            newBQuantity = calculateBQuantityString(
                parseFloat(formData.items_per_pack),
                formData.unit_of_measure,
                parseFloat(formData.total_quantity)
            );
            setCalculatedBQuantity(newBQuantity);
        } else {
            setCalculatedBQuantity('');
        }
        
        setFormData(prev => ({
            ...prev,
            cost_per_unit: newCostPerUnit,
            sell_per_pack: newSellPerPack,
            bquantity: newBQuantity
        }));
    }, [formData.cost_price_per_pack, formData.sell_price_per_unit, formData.items_per_pack, formData.unit_of_measure, formData.total_quantity]);

    // Load existing batch numbers
    useEffect(() => {
        const loadExistingBatchNumbers = async () => {
            if (!currentShop) return;
            
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_all_products',
                        username: user?.username,
                        shop_id: currentShop.shop_id
                    })
                });
                
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    const batchNos = data.data
                        .map(p => p.batch_no)
                        .filter(b => b && b.length >= 7);
                    setFormData(prev => ({ ...prev, existing_batch_nos: batchNos }));
                }
            } catch (error) {
                console.error('Error loading existing batch numbers:', error);
            }
        };
        
        loadExistingBatchNumbers();
    }, [currentShop, user?.username]);

    // Auto-generate batch number
    useEffect(() => {
        if (!isEditMode && 
            formData.product_name && 
            formData.product_name.trim() !== '' && 
            !loading && 
            !isClearingRef.current &&
            !formData.batch_no_manual) {
            
            const newBatchNo = generateBatchNoFromProductName(
                formData.product_name,
                formData.existing_batch_nos || []
            );
            
            if (formData.batch_no !== newBatchNo) {
                setFormData(prev => ({ ...prev, batch_no: newBatchNo }));
                const message = (translations.batchAutoGenerated || 'Batch number auto-generated: ') + newBatchNo;
                showToast('info', message);
            }
        }
    }, [formData.product_name, isEditMode, loading, translations, formData.existing_batch_nos, showToast]);

    // Auto-generate barcode
    useEffect(() => {
        if (!isEditMode && 
            formData.product_name && 
            formData.product_name.trim() !== '' && 
            (!formData.barcode || formData.barcode.trim() === '') &&
            !loading && 
            !isClearingRef.current &&
            !scannedBarcodeRef.current &&
            !formData.barcode_manual &&
            user?.username && 
            currentShop?.shop_id) {

            const timer = setTimeout(() => {
                const newBarcode = generateBarcodeFromProductName(
                    formData.product_name,
                    user.username,
                    currentShop.shop_id
                );

                if (newBarcode) {
                    setFormData(prev => {
                        // A scan/manual entry may have arrived while the debounce
                        // was pending, so check the latest state before writing.
                        if (scannedBarcodeRef.current || prev.barcode_manual || prev.barcode?.trim()) return prev;
                        return { ...prev, barcode: newBarcode };
                    });
                }
            }, 250);

            return () => clearTimeout(timer);
        }
    }, [formData.product_name, formData.barcode, formData.barcode_manual, isEditMode, loading, user?.username, currentShop?.shop_id]);

    // Clear batch/barcode if product name cleared
    useEffect(() => {
        if (!isEditMode && (!formData.product_name || formData.product_name.trim() === '') && !loading) {
            if (formData.batch_no !== '' && !formData.batch_no_manual) {
                setFormData(prev => ({ ...prev, batch_no: '' }));
            }
            if (formData.barcode !== '' && !formData.barcode_manual) {
                setFormData(prev => ({ ...prev, barcode: '' }));
            }
        }
    }, [formData.product_name, isEditMode, loading]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // Language sync
    useEffect(() => {
        if (user?.language && user.language !== currentLanguage) {
            setCurrentLanguage(user.language);
        }
    }, [user?.language]);

    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await productsTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Load product if editing
    useEffect(() => {
        if (productId) {
            setEntryMode('single');
            setIsEditMode(true);
            loadProductData(productId);
        }
    }, [productId]);

    // Load shops
    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                console.error('Error loading shops:', error);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // ==============================================
    // HANDLER FUNCTIONS
    // ==============================================
    
    const selectCategory = (category) => {
        setFormData(prev => ({ ...prev, category: category.value }));
        setCategorySearchTerm(category.label);
        setShowCategoryDropdown(false);
    };

    const selectBulkCategory = (category) => {
        setBulkFormData(prev => ({ ...prev, category: category.value }));
        setBulkCategorySearchTerm(category.label);
        setShowBulkCategoryDropdown(false);
        setBulkErrors(prev => ({ ...prev, category: '' }));
    };

    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `cosmetics_product_drafts_${userId}_${shopId}`;
    };

    const readStoredDrafts = () => {
        try {
            const value = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
            return Array.isArray(value) ? value : [];
        } catch (error) {
            console.error('Unable to read product drafts:', error);
            return [];
        }
    };

    const loadDrafts = () => {
        setSavedDrafts(readStoredDrafts());
    };

    const saveDraft = () => {
        const hasSingleData = entryMode === 'single' && (
            formData.product_name.trim() || formData.barcode.trim() || formData.batch_no.trim()
        );
        const hasBulkData = entryMode === 'bulk' && (
            bulkFormData.product_name.trim() || bulkRowsRef.current.length > 0
        );

        if (!hasSingleData && !hasBulkData) {
            showToast('warning', 'Cannot save an empty product draft.');
            return;
        }

        const draft = {
            id: Date.now(),
            mode: entryMode,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id || null,
            name: entryMode === 'bulk'
                ? (bulkFormData.product_name.trim() || 'Bulk products')
                : (formData.product_name.trim() || 'Single product'),
            formData: entryMode === 'single' ? formData : null,
            categorySearchTerm: entryMode === 'single' ? categorySearchTerm : '',
            bulkFormData: entryMode === 'bulk' ? bulkFormData : null,
            bulkRows: entryMode === 'bulk' ? bulkRowsRef.current : [],
            bulkCategorySearchTerm: entryMode === 'bulk' ? bulkCategorySearchTerm : ''
        };

        try {
            const drafts = readStoredDrafts();
            localStorage.setItem(getDraftStorageKey(), JSON.stringify([draft, ...drafts]));
            loadDrafts();
            showToast('success', 'Product draft saved.');
        } catch (error) {
            console.error('Unable to save product draft:', error);
            showToast('error', 'Draft is too large for browser storage. Save the products or reduce stored drafts.');
        }
    };

    const loadDraft = (draft) => {
        if (!draft) return;

        const mode = draft.mode === 'bulk' ? 'bulk' : 'single';
        setEntryMode(mode);
        setIsEditMode(false);
        setErrors({});
        setBulkErrors({});
        setLastScannedBarcode('');

        if (mode === 'bulk') {
            const restoredRows = Array.isArray(draft.bulkRows) ? draft.bulkRows : [];
            setBulkFormData({ ...createInitialBulkForm(), ...(draft.bulkFormData || {}) });
            bulkRowsRef.current = restoredRows;
            setBulkRows(restoredRows);
            setBulkCategorySearchTerm(draft.bulkCategorySearchTerm || '');
            setBulkPage(0);
        } else {
            const restoredForm = draft.formData || {};
            setFormData(prev => ({
                ...prev,
                ...restoredForm,
                record_id: null,
                date: restoredForm.date || new Date().toISOString().split('T')[0]
            }));
            setCategorySearchTerm(draft.categorySearchTerm || '');
            scannedBarcodeRef.current = restoredForm.barcode || '';
        }

        setShowDraftsList(false);
        showToast('success', 'Product draft loaded.');
        setTimeout(() => {
            if (mode === 'bulk') document.getElementById('bulk_product_name')?.focus();
            else productNameRef.current?.focus();
        }, 0);
    };

    const deleteDraft = (draftId) => {
        try {
            const remaining = readStoredDrafts().filter(draft => draft.id !== draftId);
            localStorage.setItem(getDraftStorageKey(), JSON.stringify(remaining));
            setSavedDrafts(remaining);
            showToast('success', 'Product draft deleted.');
        } catch (error) {
            console.error('Unable to delete product draft:', error);
            showToast('error', 'Draft could not be deleted.');
        }
    };

    useEffect(() => {
        loadDrafts();
        setShowDraftsList(false);
    }, [currentShop?.shop_id, user?.user_id, user?.username]);

    const handleEntryModeChange = (mode) => {
        if (mode === entryMode || loading) return;
        if (isEditMode && mode === 'bulk') {
            showToast('warning', 'Finish or clear the current edit before opening Bulk entry.');
            return;
        }

        setEntryMode(mode);
        setErrors({});
        setBulkErrors({});
        setShowCategoryDropdown(false);
        setShowBulkCategoryDropdown(false);
        setBulkPage(0);
        setLastScannedBarcode('');

        setTimeout(() => {
            if (mode === 'single') {
                productNameRef.current?.focus();
            } else {
                document.getElementById('bulk_product_name')?.focus();
            }
        }, 0);
    };

    const handleBulkFieldChange = (e) => {
        const { name, value } = e.target;

        if (name === 'rq') {
            const parsedValue = value === '' ? 0 : Number(value);
            const numericValue = Number.isSafeInteger(parsedValue) && parsedValue > 0 ? parsedValue : 0;
            setBulkFormData(prev => ({ ...prev, rq: value === '' ? '' : String(numericValue) }));
            const currentRows = bulkRowsRef.current;
            const nextRows = Array.from({ length: numericValue }, (_, index) =>
                currentRows[index] || createBulkRow(bulkFormData.product_name, currentShop?.shop_id, index)
            );
            bulkRowsRef.current = nextRows;
            setBulkRows(nextRows);
            setBulkPage(0);
        } else if (name === 'product_name') {
            setBulkFormData(prev => ({ ...prev, product_name: value }));
            setBulkRows(prevRows => {
                const nextRows = prevRows.map((row, index) => ({
                    ...row,
                    batch_no: generateBulkBatchNo(value, currentShop?.shop_id, index)
                }));
                bulkRowsRef.current = nextRows;
                return nextRows;
            });
        } else if (name === 'cost_price_per_pack') {
            setBulkFormData(prev => {
                const cost = parseFloat(value);
                const profit = parseFloat(prev.profit_percent);
                const next = { ...prev, cost_price_per_pack: value };
                if (Number.isFinite(cost) && Number.isFinite(profit)) {
                    next.sell_price_per_unit = formatEditableNumber(cost * (1 + (profit / 100)), 2);
                }
                return next;
            });
        } else if (name === 'profit_percent') {
            setBulkFormData(prev => {
                const cost = parseFloat(prev.cost_price_per_pack);
                const profit = parseFloat(value);
                const next = { ...prev, profit_percent: value };
                if (Number.isFinite(cost) && Number.isFinite(profit)) {
                    next.sell_price_per_unit = formatEditableNumber(cost * (1 + (profit / 100)), 2);
                }
                return next;
            });
        } else if (name === 'sell_price_per_unit') {
            setBulkFormData(prev => {
                const cost = parseFloat(prev.cost_price_per_pack);
                const sell = parseFloat(value);
                const next = { ...prev, sell_price_per_unit: value };
                if (Number.isFinite(cost) && cost > 0 && Number.isFinite(sell)) {
                    next.profit_percent = formatEditableNumber(((sell - cost) / cost) * 100);
                }
                return next;
            });
        } else {
            setBulkFormData(prev => ({ ...prev, [name]: value }));
        }

        if (bulkErrors[name]) {
            setBulkErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleBulkRowChange = (rowIndex, field, value) => {
        setBulkRows(prev => {
            const nextRows = prev.map((row, index) =>
                index === rowIndex ? { ...row, [field]: value } : row
            );
            bulkRowsRef.current = nextRows;
            return nextRows;
        });

        setBulkErrors(prev => ({
            ...prev,
            rows: '',
            [`${field}_${rowIndex}`]: ''
        }));
    };

    const handleBulkCellKeyDown = (event, rowIndex, field) => {
        if (event.key !== 'Tab' || event.shiftKey || field !== 'company') return;
        if (rowIndex >= bulkRows.length - 1) return;

        event.preventDefault();
        focusBulkCell(rowIndex + 1, 'batch_no');
    };

    const validateBulkForm = () => {
        const nextErrors = {};
        const rq = Number(bulkFormData.rq);

        if (!bulkFormData.product_name.trim() || bulkFormData.product_name.trim().length < 2) {
            nextErrors.product_name = 'Product name must be at least 2 characters.';
        }
        if (!Number.isSafeInteger(rq) || rq < 1) {
            nextErrors.rq = 'RQ must be a positive whole number.';
        }
        if (bulkRows.length !== rq) {
            nextErrors.rows = 'RQ does not match the generated rows.';
        }
        if (!bulkFormData.cost_price_per_pack || parseFloat(bulkFormData.cost_price_per_pack) <= 0) {
            nextErrors.cost_price_per_pack = 'Cost price must be greater than zero.';
        }
        if (bulkFormData.profit_percent !== '' && (!Number.isFinite(parseFloat(bulkFormData.profit_percent)) || parseFloat(bulkFormData.profit_percent) < 0)) {
            nextErrors.profit_percent = 'Profit percentage must be zero or greater.';
        }
        if (!bulkFormData.sell_price_per_unit || parseFloat(bulkFormData.sell_price_per_unit) <= 0) {
            nextErrors.sell_price_per_unit = 'Sell price must be greater than zero.';
        }

        const seenBarcodes = new Map();
        const seenBatches = new Map();
        let firstInvalidRow = -1;
        bulkRows.forEach((row, index) => {
            const barcode = row.barcode.trim().toUpperCase();
            const batchNo = row.batch_no.trim().toUpperCase();
            if (!batchNo) {
                nextErrors[`batch_no_${index}`] = 'Batch number is required.';
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else if (seenBatches.has(batchNo)) {
                nextErrors[`batch_no_${index}`] = `Duplicates row ${seenBatches.get(batchNo) + 1}.`;
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else {
                seenBatches.set(batchNo, index);
            }

            if (!barcode) {
                nextErrors[`barcode_${index}`] = 'Scan a barcode.';
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else if (seenBarcodes.has(barcode)) {
                nextErrors[`barcode_${index}`] = `Already used in row ${seenBarcodes.get(barcode) + 1}.`;
                if (firstInvalidRow === -1) firstInvalidRow = index;
            } else {
                seenBarcodes.set(barcode, index);
            }

            const quantity = parseFloat(row.total_quantity);
            if (!Number.isFinite(quantity) || quantity <= 0) {
                nextErrors[`total_quantity_${index}`] = 'Quantity must be greater than zero.';
                if (firstInvalidRow === -1) firstInvalidRow = index;
            }
        });

        if (firstInvalidRow !== -1) {
            nextErrors.rows = 'Fix the highlighted bulk row fields.';
            setTimeout(() => bulkCellRefs.current[`${firstInvalidRow}-barcode`]?.focus(), 0);
        }

        setBulkErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentShop?.shop_id) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }
        if (!validateBulkForm()) return;
        if (isSubmittingRef.current) {
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        isSubmittingRef.current = true;
        setLoading(true);

        try {
            const payload = {
                action: 'add_bulk',
                username: user?.username || '',
                shop_id: currentShop.shop_id,
                date: bulkFormData.date,
                product_name: bulkFormData.product_name.trim(),
                rq: Number(bulkFormData.rq),
                category: bulkFormData.category || '',
                company: bulkFormData.company.trim(),
                cost_price_per_pack: bulkFormData.cost_price_per_pack,
                profit_percent: bulkFormData.profit_percent,
                sell_price_per_unit: bulkFormData.sell_price_per_unit,
                rows: bulkRows.map(row => ({
                    batch_no: row.batch_no.trim(),
                    product_name: bulkFormData.product_name.trim(),
                    total_quantity: row.total_quantity,
                    barcode: row.barcode.trim(),
                    company: bulkFormData.company.trim()
                }))
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const responseText = await response.text();
            if (!response.ok) {
                throw new Error(`Server responded with status ${response.status}: ${responseText.substring(0, 200)}`);
            }

            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                throw new Error('Invalid JSON response from server.');
            }

            if (data.status !== 'success') {
                throw new Error(data.message || 'Bulk products could not be saved.');
            }

            showToast('success', data.message || `${bulkRows.length} products added successfully.`);
            const firstId = data.products?.[0]?.id || null;
            setBulkFormData(createInitialBulkForm());
            bulkRowsRef.current = [];
            setBulkRows([]);
            setBulkErrors({});
            setBulkCategorySearchTerm('');
            setBulkPage(0);
            bulkCellRefs.current = {};
            setLastScannedBarcode('');
            if (onSuccess) onSuccess(firstId);
            setTimeout(() => document.getElementById('bulk_product_name')?.focus(), 0);
        } catch (error) {
            console.error('Bulk submit error:', error);
            showToast('error', error.message || 'Bulk products could not be saved.');
        } finally {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    };

    const selectColor = (color) => {
        setFormData(prev => ({ ...prev, color: color.value }));
        setColorSearchTerm(color.label);
        setShowColorDropdown(false);
    };

    const selectSize = (size) => {
        setFormData(prev => ({ ...prev, size: size.value }));
        setSizeSearchTerm(size.label);
        setShowSizeDropdown(false);
    };

    const validateForm = (values = formData) => {
        const newErrors = {};

        if (!values.product_name?.trim()) {
            newErrors.product_name = translations.nameRequired || 'Product name is required';
        } else if (values.product_name.trim().length < 2) {
            newErrors.product_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (!values.cost_price_per_pack || parseFloat(values.cost_price_per_pack) <= 0) {
            newErrors.cost_price_per_pack = translations.costPriceRequired || 'Cost price is required';
        }

        if (values.profit_percent !== '' && (isNaN(parseFloat(values.profit_percent)) || parseFloat(values.profit_percent) < 0)) {
            newErrors.profit_percent = 'Profit percentage must be zero or greater';
        }

        if (!values.sell_price_per_unit || parseFloat(values.sell_price_per_unit) <= 0) {
            newErrors.sell_price_per_unit = translations.sellingPriceRequired || 'Selling price is required';
        }

        if (!values.total_quantity || parseFloat(values.total_quantity) < 0) {
            newErrors.total_quantity = translations.quantityRequired || 'Quantity is required';
        }

        if (values.expiry_date) {
            const expiry = new Date(values.expiry_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (expiry < today) {
                newErrors.expiry_date = translations.expiryPast || 'Expiry date cannot be in the past';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        const finalBatchNo = !isEditMode && formData.product_name?.trim() && !formData.batch_no?.trim()
            ? generateBatchNoFromProductName(formData.product_name, formData.existing_batch_nos || [])
            : formData.batch_no;
        const finalBarcode = scannedBarcodeRef.current || (
            !isEditMode && formData.product_name?.trim() && !formData.barcode?.trim()
                ? generateBarcodeFromProductName(formData.product_name, user?.username, currentShop?.shop_id)
                : formData.barcode.trim()
        );
        const submitValues = { ...formData, batch_no: finalBatchNo, barcode: finalBarcode };

        if (finalBatchNo !== formData.batch_no || finalBarcode !== formData.barcode) {
            setFormData(prev => ({ ...prev, batch_no: finalBatchNo, barcode: finalBarcode }));
        }

        if (!validateForm(submitValues)) return;

        if (isSubmittingRef.current) {
            showToast('warning', 'Please wait, submission in progress...');
            return;
        }

        const now = Date.now();
        if (now - lastSubmitTimeRef.current < 3000) {
            showToast('warning', 'Please wait before submitting again');
            return;
        }

        const currentProductData = JSON.stringify({
            product_name: submitValues.product_name,
            batch_no: submitValues.batch_no,
            barcode: submitValues.barcode,
            product_code: submitValues.product_code,
            shop_id: currentShop.shop_id
        });

        if (!isEditMode && currentProductData === lastProductDataRef.current) {
            showToast('warning', 'This product appears to be a duplicate. Please check.');
            return;
        }

        if (!isEditMode && isDuplicateRequest({
            action: 'add',
            product_name: submitValues.product_name,
            batch_no: submitValues.batch_no,
            barcode: submitValues.barcode,
            product_code: submitValues.product_code,
            shop_id: currentShop.shop_id
        })) {
            showToast('warning', 'Please wait, a similar product is being added...');
            return;
        }

        lastProductDataRef.current = currentProductData;
        lastSubmitTimeRef.current = now;

        isSubmittingRef.current = true;
        setLoading(true);

        const submitButton = document.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        try {
            const currentUsername = user?.username || 'Unknown';
            
            const productData = {
                action: isEditMode ? 'edit' : 'add',
                product_name: submitValues.product_name,
                product_code: submitValues.product_code,
                batch_no: submitValues.batch_no || '',
                barcode: submitValues.barcode || '',
                category: submitValues.category || '',
                company: submitValues.company || '',
                color: submitValues.color || '',
                size: submitValues.size || '',
                date: submitValues.date,
                expiry_date: submitValues.expiry_date || null,
                quantity_alert: '2',
                total_quantity: submitValues.total_quantity,
                packing_type: 'packet',
                sub_packing_type: 'piece',
                items_per_pack: 1,
                unit_of_measure: 'piece',
                cost_price_per_pack: submitValues.cost_price_per_pack,
                profit_percent: submitValues.profit_percent,
                sell_price_per_unit: submitValues.sell_price_per_unit,
                purchase_type: 'packing',
                username: currentUsername,
                shop_id: currentShop.shop_id,
                _timestamp: Date.now()
            };

            const effectiveProductId = productId || submitValues.record_id;
            if (isEditMode && effectiveProductId) {
                productData.id = effectiveProductId;
            }

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(productData),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Server error response:', errorText);
                throw new Error(`Server responded with status ${response.status}: ${errorText.substring(0, 200)}`);
            }

            const responseText = await response.text();
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error. Raw response:', responseText.substring(0, 500));
                throw new Error('Invalid JSON response from server. Please check the server logs.');
            }

            if (data.status === 'duplicate' || data.duplicate_prevention) {
                showToast('warning', data.message || 'Duplicate submission prevented. Please wait.');
                lastProductDataRef.current = '';
                return;
            }

            if (data.status === 'success') {
                showToast('success', isEditMode
                    ? (translations.productUpdated || 'Product updated successfully!')
                    : (translations.productSaved || 'Product saved successfully!'));

                if (!isEditMode && formData.batch_no) {
                    setFormData(prev => ({
                        ...prev,
                        existing_batch_nos: [...prev.existing_batch_nos, formData.batch_no]
                    }));
                }

                if (!isEditMode) {
                    scannedBarcodeRef.current = '';
                    setFormData(prev => ({
                        record_id: null,
                        date: new Date().toISOString().split('T')[0],
                        batch_no: '',
                        barcode: '',
                        product_name: '',
                        product_code: '',
                        category: '',
                        company: '',
                        color: '',
                        size: '',
                        packing_type: 'packet',
                        sub_packing_type: 'piece',
                        items_per_pack: '1',
                        unit_of_measure: 'piece',
                        cost_price_per_pack: '',
                        profit_percent: '',
                        sell_price_per_unit: '',
                        total_quantity: '',
                        quantity_alert: '2',
                        expiry_date: '',
                        cost_per_unit: '',
                        sell_per_pack: '',
                        bquantity: '',
                        purchase_type: 'packing',
                        themeColor: prev.themeColor,
                        batch_no_manual: false,
                        barcode_manual: false,
                        existing_batch_nos: prev.existing_batch_nos
                    }));
                    setCategorySearchTerm('');
                    setColorSearchTerm('');
                    setSizeSearchTerm('');
                    setErrors({});
                    lastProductDataRef.current = '';
                    setCalculatedCostPerUnit('');
                    setCalculatedSellPerPack('');
                    setCalculatedBQuantity('');
                    
                    setTimeout(() => {
                        if (productNameRef.current) {
                            productNameRef.current.focus();
                        }
                    }, 100);
                }

                if (onSuccess) onSuccess(data.productid || productId);
            } else {
                showToast('error', data.message || (isEditMode
                    ? translations.failedUpdate
                    : translations.failedAdd) || 'Operation failed');
                lastProductDataRef.current = '';
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                showToast('error', 'Request timeout. Please try again.');
            } else {
                console.error('Submit error:', error);
                showToast('error', error.message || translations.networkError || 'Network error');
            }
            lastProductDataRef.current = '';
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
                if (submitButton) submitButton.disabled = false;
            }, 3000);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'cost_price_per_pack') {
            setFormData(prev => {
                const cost = parseFloat(value);
                const profit = parseFloat(prev.profit_percent);
                const next = { ...prev, cost_price_per_pack: value };
                if (Number.isFinite(cost) && Number.isFinite(profit)) {
                    next.sell_price_per_unit = formatEditableNumber(cost * (1 + (profit / 100)), 2);
                }
                return next;
            });
        } else if (name === 'profit_percent') {
            setFormData(prev => {
                const cost = parseFloat(prev.cost_price_per_pack);
                const profit = parseFloat(value);
                const next = { ...prev, profit_percent: value };
                if (Number.isFinite(cost) && Number.isFinite(profit)) {
                    next.sell_price_per_unit = formatEditableNumber(cost * (1 + (profit / 100)), 2);
                }
                return next;
            });
        } else if (name === 'sell_price_per_unit') {
            setFormData(prev => {
                const cost = parseFloat(prev.cost_price_per_pack);
                const sell = parseFloat(value);
                const next = { ...prev, sell_price_per_unit: value };
                if (Number.isFinite(cost) && cost > 0 && Number.isFinite(sell)) {
                    next.profit_percent = formatEditableNumber(((sell - cost) / cost) * 100);
                }
                return next;
            });
        } else if (name === 'cost_per_unit' || name === 'sell_per_pack' || name === 'bquantity') {
            return;
        } else if (name === 'product_name') {
            setFormData(prev => ({
                ...prev,
                product_name: value,
                barcode: scannedBarcodeRef.current || prev.barcode,
                barcode_manual: scannedBarcodeRef.current ? true : prev.barcode_manual
            }));
        } else if (name === 'batch_no') {
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                batch_no_manual: true
            }));
        } else if (name === 'barcode') {
            scannedBarcodeRef.current = value.trim();
            setFormData(prev => ({ 
                ...prev, 
                [name]: value,
                barcode_manual: true
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleClear = () => {
        isClearingRef.current = true;
        scannedBarcodeRef.current = '';

        if (entryMode === 'bulk') {
            setBulkFormData(createInitialBulkForm());
            bulkRowsRef.current = [];
            setBulkRows([]);
            setBulkErrors({});
            setBulkCategorySearchTerm('');
            setShowBulkCategoryDropdown(false);
            setBulkPage(0);
            bulkCellRefs.current = {};
            setLastScannedBarcode('');
            showToast('success', translations.draftCleared || 'Form cleared');
            setTimeout(() => document.getElementById('bulk_product_name')?.focus(), 100);
            setTimeout(() => { isClearingRef.current = false; }, 500);
            return;
        }

        setFormData({
            record_id: null,
            date: new Date().toISOString().split('T')[0],
            batch_no: '',
            barcode: '',
            product_name: '',
            product_code: '',
            category: '',
            company: '',
            color: '',
            size: '',
            packing_type: 'packet',
            sub_packing_type: 'piece',
            items_per_pack: '1',
            unit_of_measure: 'piece',
            cost_price_per_pack: '',
            profit_percent: '',
            sell_price_per_unit: '',
            total_quantity: '',
            quantity_alert: '2',
            expiry_date: '',
            cost_per_unit: '',
            sell_per_pack: '',
            bquantity: '',
            purchase_type: 'packing',
            themeColor: formData.themeColor,
            batch_no_manual: false,
            barcode_manual: false,
            existing_batch_nos: formData.existing_batch_nos || []
        });
        setCategorySearchTerm('');
        setColorSearchTerm('');
        setSizeSearchTerm('');
        setErrors({});
        setIsEditMode(false);
        lastProductDataRef.current = '';
        setCalculatedCostPerUnit('');
        setCalculatedSellPerPack('');
        setCalculatedBQuantity('');

        showToast('success', translations.draftCleared || 'Form cleared');

        setTimeout(() => {
            if (productNameRef.current) {
                productNameRef.current.focus();
            }
        }, 100);

        setTimeout(() => {
            isClearingRef.current = false;
        }, 500);
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                setCalculatedCostPerUnit('');
                setCalculatedSellPerPack('');
                setCalculatedBQuantity('');
                setCategorySearchTerm('');
                setColorSearchTerm('');
                setSizeSearchTerm('');
                setFormData(prev => ({ ...prev, existing_batch_nos: [] }));
                setBulkRows(prev => {
                    const nextRows = prev.map((row, index) => ({
                        ...row,
                        batch_no: generateBulkBatchNo(bulkFormData.product_name, shop.shop_id, index)
                    }));
                    bulkRowsRef.current = nextRows;
                    return nextRows;
                });
                setBulkPage(0);
                
                const loadExistingBatchNumbers = async () => {
                    try {
                        const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                action: 'get_all_products',
                                username: user?.username,
                                shop_id: shop.shop_id
                            })
                        });
                        const data = await response.json();
                        if (data.status === 'success' && data.data) {
                            const batchNos = data.data
                                .map(p => p.batch_no)
                                .filter(b => b && b.length >= 7);
                            setFormData(prev => ({ ...prev, existing_batch_nos: batchNos }));
                        }
                    } catch (error) {
                        console.error('Error loading existing batch numbers:', error);
                    }
                };
                loadExistingBatchNumbers();
            }
            showToast(success ? 'success' : 'error',
                success ? (translations.shopSwitched || 'Shop switched successfully')
                    : (translations.shopSwitchFailed || 'Failed to switch shop'));
        } catch (error) {
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    const loadProductData = async (id) => {
        if (!currentShop) {
            showToast('error', translations.selectShop || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    username: user?.username,
                    shop_id: currentShop.shop_id
                })
            });

            const responseText = await response.text();
            
            if (!responseText || responseText.trim() === '') {
                throw new Error('Empty response from server');
            }
            
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error('JSON Parse Error:', responseText);
                throw new Error('Invalid JSON response from server');
            }

            if (data.status === 'success' && data.data) {
                scannedBarcodeRef.current = data.data.barcode || '';
                setFormData(prev => ({
                    ...prev,
                    record_id: data.data.id || productId,
                    date: data.data.date || new Date().toISOString().split('T')[0],
                    batch_no: data.data.batch_no || '',
                    barcode: data.data.barcode || '',
                    product_name: data.data.product_name || '',
                    product_code: data.data.product_code || '',
                    category: data.data.category || '',
                    company: data.data.company || '',
                    color: data.data.color || '',
                    size: data.data.size || '',
                    packing_type: data.data.packing_type || 'packet',
                    sub_packing_type: data.data.sub_packing_type || 'piece',
                    items_per_pack: data.data.items_per_pack || '1',
                    unit_of_measure: data.data.unit_of_measure || 'piece',
                    cost_price_per_pack: data.data.cost_per_pack || '',
                    profit_percent: getStoredOrCalculatedProfitPercent(data.data),
                    sell_price_per_unit: data.data.sell_price_per_unit || '',
                    total_quantity: data.data.quantity || '',
                    quantity_alert: data.data.quantity_alert || '2',
                    expiry_date: data.data.expiry_date || '',
                    cost_per_unit: data.data.cost_per_unit || '',
                    sell_per_pack: data.data.sell_per_pack || '',
                    bquantity: data.data.bquantity || '',
                    themeColor: prev.themeColor,
                    batch_no_manual: true,
                    barcode_manual: true
                }));
                
                const selectedCategory = categoryOptions.find(c => c.value === data.data.category);
                setCategorySearchTerm(selectedCategory ? selectedCategory.label : '');
                const selectedColor = colorOptions.find(c => c.value === data.data.color);
                setColorSearchTerm(selectedColor ? selectedColor.label : '');
                const selectedSize = sizeOptions.find(s => s.value === data.data.size);
                setSizeSearchTerm(selectedSize ? selectedSize.label : '');
                
                if (data.data.cost_per_unit) setCalculatedCostPerUnit(data.data.cost_per_unit);
                if (data.data.sell_per_pack) setCalculatedSellPerPack(data.data.sell_per_pack);
                if (data.data.bquantity) setCalculatedBQuantity(data.data.bquantity);
            } else {
                showToast('error', data.message || 'Failed to load product data');
            }
        } catch (error) {
            console.error('Load product error:', error);
            showToast('error', translations.networkError || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // THEME AND DARK MODE EFFECTS
    // ==============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }
        
        let style = document.getElementById('dynamic-orb-styles-product');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-product';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;
        
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
        }, 500);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentThemeColor]);

    // Dark mode
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozui-dark');
                } else {
                    container.classList.remove('ozui-dark');
                }
            }
            
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozui-dark');
                    } else {
                        container.classList.remove('ozui-dark');
                    }
                }
                
                if (isDark) {
                    document.body.classList.add('ozui-dark');
                    document.documentElement.classList.add('ozui-dark');
                } else {
                    document.body.classList.remove('ozui-dark');
                    document.documentElement.classList.remove('ozui-dark');
                }
            } else {
                syncDarkMode();
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // Language
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await productsTranslator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // RTL
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozui-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozui-shop-indicator no-shop">
                    <div className="ozui-shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="ozui-shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="ozui-header-shop-btn"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        
        return (
            <div className="ozui-shop-indicator">
                {/* Shop indicator content - kept empty as original */}
            </div>
        );
    };

    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Hidden Barcode Input for scanner focus - completely invisible */}
            <input
                ref={barcodeInputRef}
                type="text"
                id="barcode_scanner_hidden"
                name="barcode_scanner_hidden"
                style={{ 
                    position: 'absolute', 
                    left: '-9999px', 
                    opacity: 0, 
                    width: '1px', 
                    height: '1px',
                    pointerEvents: 'none'
                }}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                onChange={() => {}}
            />

            {/* Modern Animated Sticky Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations.editProduct || 'Edit Stock') : entryMode === 'bulk' ? 'Bulk Stock Entry' : (translations.addProduct || 'Add Stock')}</h1>
                            <p>{translations.fillDetails || 'Fill in the stock details below'}</p>
                           
                        </div>
                    </div>
                     
                    <div className="ozui-header-right">
                        {renderShopIndicator()}
                         
                        <div className="ozui-header-actions">
                            <button 
                                type="button"
                                className="ozui-action-btn clear-draft" 
                                onClick={handleClear}
                                disabled={loading}
                                title={translations.clearButton || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                <span>{translations.clearButton || 'Clear'}</span>
                            </button>
                            <button
                                type="button"
                                className="ozui-action-btn save-draft"
                                onClick={saveDraft}
                                disabled={loading}
                                title={translations.saveDraft || 'Save Draft'}
                            >
                                <FontAwesomeIcon icon={faSave} />
                                <span>{translations.saveDraft || 'Save Draft'}</span>
                            </button>
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    type="button"
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(open => !open)}
                                    disabled={loading}
                                    title={translations.viewDrafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations.savedDrafts || 'Saved Product Drafts'}</h4>
                                            <button type="button" onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : savedDrafts.map(draft => (
                                                <div className="draft-item" key={draft.id}>
                                                    <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                        <div className="draft-name">{draft.name || 'Product draft'}</div>
                                                        <div className="draft-details">
                                                            <span>{draft.mode === 'bulk' ? 'Bulk' : 'Single'}</span>
                                                            {draft.mode === 'bulk' && <span>{draft.bulkRows?.length || 0} rows</span>}
                                                        </div>
                                                        <div className="draft-date">{new Date(draft.savedAt).toLocaleString()}</div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="delete-draft-btn"
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            deleteDraft(draft.id);
                                                        }}
                                                        title="Delete draft"
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {!isEditMode && (
                <div className="ozui-entry-mode-bar">
                    <div className="ozui-entry-mode-switch" role="group" aria-label="Product entry mode">
                        <button
                            type="button"
                            className={`ozui-entry-mode-btn ${entryMode === 'single' ? 'ozui-active' : ''}`}
                            onClick={() => handleEntryModeChange('single')}
                            aria-pressed={entryMode === 'single'}
                            disabled={loading}
                        >
                            Single
                        </button>
                        <button
                            type="button"
                            className={`ozui-entry-mode-btn ${entryMode === 'bulk' ? 'ozui-active' : ''}`}
                            onClick={() => handleEntryModeChange('bulk')}
                            aria-pressed={entryMode === 'bulk'}
                            disabled={loading}
                        >
                            Bulk
                        </button>
                    </div>
                </div>
            )}

            {entryMode === 'single' ? (
            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    {/* Date */}
                    <div className={`ozui-field ${errors.date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations.date || 'Date'}
                            </label>
                        </div>
                    </div>

                    {/* Product Name */}
                    <div className={`ozui-field ${errors.product_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={productNameRef}
                                type="text"
                                name="product_name"
                                id="product_name"
                                value={formData.product_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="product_name">
                                <FontAwesomeIcon icon={faBox} />
                                {translations.productName || 'Product Name'} <span>*</span>
                            </label>
                        </div>
                        {errors.product_name && <small>{errors.product_name}</small>}
                    </div>

                    {/* Batch Number */}
                    <div className={`ozui-field ${errors.batch_no ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="batch_no"
                                id="batch_no"
                                value={formData.batch_no}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading || isEditMode}
                                className={!isEditMode && formData.batch_no && !formData.batch_no_manual ? 'auto-filled' : ''}
                            />
                            <label htmlFor="batch_no">
                                <FontAwesomeIcon icon={faTag} />
                                {translations.batchNumber || 'Batch Number'} 
                                {!isEditMode && !formData.batch_no && <span className="auto-hint">(Auto from product name - increments)</span>}
                                {!isEditMode && formData.batch_no && !formData.batch_no_manual && <span className="auto-generated">(Auto-generated - increments)</span>}
                                {!isEditMode && formData.batch_no && formData.batch_no_manual && <span className="manual-edited">(Manually edited)</span>}
                                {isEditMode && <span>(Read only)</span>}
                            </label>
                        </div>
                        {errors.batch_no && <small>{errors.batch_no}</small>}
                    </div>

                    {/* Barcode - with scanner support */}
                    <div className={`ozui-field ${errors.barcode ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="barcode"
                                id="barcode"
                                value={formData.barcode}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading || isEditMode}
                                className={!isEditMode && formData.barcode && !formData.barcode_manual ? 'auto-filled' : ''}
                                autoComplete="off"
                            />
                            <label htmlFor="barcode">
                                <FontAwesomeIcon icon={faBarcode} />
                                {translations.barcode || 'Barcode'} 
                                {!isEditMode && !formData.barcode && <span className="auto-hint">(Auto from product name - or scan)</span>}
                                {!isEditMode && formData.barcode && !formData.barcode_manual && <span className="auto-generated">(Auto-generated - editable)</span>}
                                {!isEditMode && formData.barcode && formData.barcode_manual && <span className="manual-edited">(Manually edited)</span>}
                                {isEditMode && <span>(Read only)</span>}
                                {isScannerActive && !isEditMode && formData.barcode && (
                                    <span className="scanner-indicator" style={{ color: '#10b981', fontSize: '0.65rem', marginLeft: '4px' }}>
                                        <FontAwesomeIcon icon={faBarcode} /> ✓
                                    </span>
                                )}
                            </label>
                        </div>
                        {errors.barcode && <small>{errors.barcode}</small>}
                    </div>

                    {/* Category */}
                    <div className={`ozui-field ${errors.category ? 'ozui-error' : ''}`} ref={categoryDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="category"
                                value={categorySearchTerm}
                                onChange={(e) => {
                                    setCategorySearchTerm(e.target.value);
                                    setShowCategoryDropdown(true);
                                    if (formData.category && !categoryOptions.find(c => 
                                        c.label.toLowerCase() === e.target.value.toLowerCase()
                                    )) {
                                        setFormData(prev => ({ ...prev, category: '' }));
                                    }
                                }}
                                onFocus={() => setShowCategoryDropdown(true)}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="off"
                            />
                            <label htmlFor="category">
                                <FontAwesomeIcon icon={faLayerGroup} />
                                {translations.category || 'Category'}
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {errors.category && <small>{errors.category}</small>}
                        {showCategoryDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredCategories.length > 0 ? (
                                    filteredCategories.map(cat => (
                                        <div
                                            key={cat.value}
                                            className={`ozui-category-option ${formData.category === cat.value ? 'ozui-selected' : ''}`}
                                            onClick={() => selectCategory(cat)}
                                        >
                                            {cat.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="ozui-category-empty">No categories found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Company */}
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="company"
                                id="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="company">
                                <FontAwesomeIcon icon={faBuilding} />
                                {translations.company || 'Company / Brand'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                    </div>

                    {/* Cost Price Per Pack */}
                    <div className={`ozui-field ${errors.cost_price_per_pack ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="cost_price_per_pack"
                                id="cost_price_per_pack"
                                value={formData.cost_price_per_pack}
                                onChange={handleChange}
                                required
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="cost_price_per_pack">
                                <FontAwesomeIcon icon={faRupeeSign} />
                                {translations.costPerPack || 'Cost Per Packet'} <span>*</span>
                            </label>
                        </div>
                        {errors.cost_price_per_pack && <small>{errors.cost_price_per_pack}</small>}
                    </div>

                    {/* Profit Percentage - updates selling price automatically */}
                    <div className={`ozui-field ${errors.profit_percent ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="profit_percent"
                                id="profit_percent"
                                value={formData.profit_percent}
                                onChange={handleChange}
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="profit_percent">
                                <FontAwesomeIcon icon={faPercent} />
                                {translations.profitMargin || 'Profit'} %
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.profit_percent && <small>{errors.profit_percent}</small>}
                    </div>

                    {/* Selling Price Per Packet */}
                    <div className={`ozui-field ${errors.sell_price_per_unit ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="sell_price_per_unit"
                                id="sell_price_per_unit"
                                value={formData.sell_price_per_unit}
                                onChange={handleChange}
                                required
                                step="0.01"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="sell_price_per_unit">
                                <FontAwesomeIcon icon={faRupeeSign} />
                                {translations.sellingPerPack || 'Sell Price Per Packet'} <span>*</span>
                            </label>
                        </div>
                        {errors.sell_price_per_unit && <small>{errors.sell_price_per_unit}</small>}
                    </div>

                    {/* Total Quantity */}
                    <div className={`ozui-field ${errors.total_quantity ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="total_quantity"
                                id="total_quantity"
                                value={formData.total_quantity}
                                onChange={handleChange}
                                required
                                step="0.001"
                                min="0"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="total_quantity">
                                <FontAwesomeIcon icon={faWarehouse} />
                                {translations.totalQuantity || 'Total Quantity'} <span>*</span>
                            </label>
                        </div>
                        {errors.total_quantity && <small>{errors.total_quantity}</small>}
                    </div>

                    {/* Expiry Date */}
                    <div className={`ozui-field ${errors.expiry_date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="expiry_date"
                                id="expiry_date"
                                value={formData.expiry_date}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="expiry_date">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {translations.expiryDate || 'Expiry Date'} 
                                <span>({translations.optional || 'Optional'})</span>
                            </label>
                        </div>
                        {errors.expiry_date && <small>{errors.expiry_date}</small>}
                    </div>
                </div>
                
                {/* Form Actions */}
                <div className="ozui-form-actions">
                    <button 
                        type="submit" 
                        className="ozui-btn ozui-btn-primary" 
                        disabled={loading || !currentShop || isSubmittingRef.current}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{isEditMode ? (translations.updating || 'Updating...') : (translations.adding || 'Adding...')}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{isEditMode ? (translations.updateProduct || 'Update Product') : (translations.addProductButton || 'Save Product')}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
            ) : (
            <form onSubmit={handleBulkSubmit} className="ozui-form ozui-bulk-form">
                <div className="ozui-bulk-summary-row">
                    <div className={`ozui-field ${bulkErrors.date ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="bulk_date"
                                value={bulkFormData.date}
                                onChange={handleBulkFieldChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_date">
                                <FontAwesomeIcon icon={faCalendarAlt} /> Date
                            </label>
                        </div>
                    </div>

                    <div className={`ozui-field ${bulkErrors.product_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="product_name"
                                id="bulk_product_name"
                                value={bulkFormData.product_name}
                                onChange={handleBulkFieldChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_product_name">
                                <FontAwesomeIcon icon={faBox} /> Product Name <span>*</span>
                            </label>
                        </div>
                        {bulkErrors.product_name && <small>{bulkErrors.product_name}</small>}
                    </div>

                    <div className={`ozui-field ${bulkErrors.rq ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="rq"
                                id="bulk_rq"
                                value={bulkFormData.rq}
                                onChange={handleBulkFieldChange}
                                min="1"
                                step="1"
                                inputMode="numeric"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_rq">
                                <FontAwesomeIcon icon={faBoxes} /> RQ (Rows) <span>*</span>
                            </label>
                        </div>
                        {bulkErrors.rq && <small>{bulkErrors.rq}</small>}
                    </div>

                    <div className={`ozui-field ${bulkErrors.category ? 'ozui-error' : ''}`} ref={bulkCategoryDropdownRef}>
                        <div className="ozui-input-wrapper ozui-category-wrapper">
                            <input
                                type="text"
                                id="bulk_category"
                                value={bulkCategorySearchTerm}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setBulkCategorySearchTerm(value);
                                    setShowBulkCategoryDropdown(true);
                                    const exactMatch = categoryOptions.find(category =>
                                        category.label.toLowerCase() === value.toLowerCase()
                                    );
                                    setBulkFormData(prev => ({ ...prev, category: exactMatch?.value || '' }));
                                }}
                                onFocus={() => setShowBulkCategoryDropdown(true)}
                                placeholder=" "
                                autoComplete="off"
                                disabled={loading}
                            />
                            <label htmlFor="bulk_category">
                                <FontAwesomeIcon icon={faLayerGroup} /> Category <span>(Optional)</span>
                            </label>
                            <FontAwesomeIcon icon={faChevronDown} className="ozui-dropdown-arrow" />
                        </div>
                        {showBulkCategoryDropdown && (
                            <div className="ozui-category-dropdown">
                                {filteredBulkCategories.length > 0 ? filteredBulkCategories.map(category => (
                                    <div
                                        key={category.value}
                                        className={`ozui-category-option ${bulkFormData.category === category.value ? 'ozui-selected' : ''}`}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => selectBulkCategory(category)}
                                    >
                                        {category.label}
                                    </div>
                                )) : <div className="ozui-category-empty">No categories found</div>}
                            </div>
                        )}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="company"
                                id="bulk_company"
                                value={bulkFormData.company}
                                onChange={handleBulkFieldChange}
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_company">
                                <FontAwesomeIcon icon={faBuilding} /> Company / Brand <span>(Optional)</span>
                            </label>
                        </div>
                    </div>

                    <div className={`ozui-field ${bulkErrors.cost_price_per_pack ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="cost_price_per_pack"
                                id="bulk_cost_price"
                                value={bulkFormData.cost_price_per_pack}
                                onChange={handleBulkFieldChange}
                                min="0"
                                step="0.01"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_cost_price">
                                <FontAwesomeIcon icon={faRupeeSign} /> Cost Price <span>*</span>
                            </label>
                        </div>
                        {bulkErrors.cost_price_per_pack && <small>{bulkErrors.cost_price_per_pack}</small>}
                    </div>

                    <div className={`ozui-field ${bulkErrors.profit_percent ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="profit_percent"
                                id="bulk_profit_percent"
                                value={bulkFormData.profit_percent}
                                onChange={handleBulkFieldChange}
                                min="0"
                                step="0.01"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_profit_percent">
                                <FontAwesomeIcon icon={faPercent} /> Profit % <span>(Optional)</span>
                            </label>
                        </div>
                        {bulkErrors.profit_percent && <small>{bulkErrors.profit_percent}</small>}
                    </div>

                    <div className={`ozui-field ${bulkErrors.sell_price_per_unit ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                name="sell_price_per_unit"
                                id="bulk_sell_price"
                                value={bulkFormData.sell_price_per_unit}
                                onChange={handleBulkFieldChange}
                                min="0"
                                step="0.01"
                                placeholder=" "
                                disabled={loading}
                            />
                            <label htmlFor="bulk_sell_price">
                                <FontAwesomeIcon icon={faRupeeSign} /> Sell Price <span>*</span>
                            </label>
                        </div>
                        {bulkErrors.sell_price_per_unit && <small>{bulkErrors.sell_price_per_unit}</small>}
                    </div>
                </div>

                <div className="ozui-bulk-toolbar" aria-live="polite">
                    <div>
                        <FontAwesomeIcon icon={faBarcode} />
                        <strong> Global scanner ready</strong>
                        <span> — focused barcode cell receives the scan; otherwise the first empty row is used.</span>
                    </div>
                    <div className="ozui-bulk-progress">
                        {scannedBulkCount} / {bulkRows.length} scanned
                        {lastScannedBarcode && <span className="ozui-last-scan">Last: {lastScannedBarcode}</span>}
                    </div>
                </div>

                {bulkErrors.rows && (
                    <div className="ozui-warning-message">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {bulkErrors.rows}
                    </div>
                )}

                <div className="ozui-bulk-table-container">
                    <table className="ozui-bulk-table">
                        <thead>
                            <tr>
                                <th className="ozui-bulk-sr-col">Sr</th>
                                <th>Batch No</th>
                                <th>Product Name</th>
                                <th>Total Quantity</th>
                                <th>Barcode</th>
                                <th>Company</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bulkRows.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="ozui-bulk-empty">
                                        Enter RQ above to create item rows.
                                    </td>
                                </tr>
                            ) : visibleBulkRows.map((row, pageRowIndex) => {
                                const rowIndex = bulkPageStart + pageRowIndex;
                                return (
                                <tr key={`${row.batch_no}-${rowIndex}`}>
                                    <td className="ozui-bulk-sr-cell">{rowIndex + 1}</td>
                                    <td>
                                        <input
                                            ref={element => { bulkCellRefs.current[`${rowIndex}-batch_no`] = element; }}
                                            className={`ozui-bulk-input ${bulkErrors[`batch_no_${rowIndex}`] ? 'ozui-invalid' : ''}`}
                                            value={row.batch_no}
                                            readOnly
                                            title={bulkErrors[`batch_no_${rowIndex}`] || 'Automatically generated unique batch number'}
                                            aria-label={`Batch number row ${rowIndex + 1}`}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            ref={element => { bulkCellRefs.current[`${rowIndex}-product_name`] = element; }}
                                            className="ozui-bulk-input ozui-bulk-readonly"
                                            value={bulkFormData.product_name}
                                            readOnly
                                            aria-label={`Product name row ${rowIndex + 1}`}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            ref={element => { bulkCellRefs.current[`${rowIndex}-total_quantity`] = element; }}
                                            className={`ozui-bulk-input ${bulkErrors[`total_quantity_${rowIndex}`] ? 'ozui-invalid' : ''}`}
                                            type="number"
                                            min="0.001"
                                            step="0.001"
                                            value={row.total_quantity}
                                            onChange={(event) => handleBulkRowChange(rowIndex, 'total_quantity', event.target.value)}
                                            disabled={loading}
                                            title={bulkErrors[`total_quantity_${rowIndex}`] || ''}
                                            aria-label={`Total quantity row ${rowIndex + 1}`}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            ref={element => { bulkCellRefs.current[`${rowIndex}-barcode`] = element; }}
                                            className={`ozui-bulk-input ozui-bulk-barcode-input ${bulkErrors[`barcode_${rowIndex}`] ? 'ozui-invalid' : ''}`}
                                            value={row.barcode}
                                            onChange={(event) => handleBulkRowChange(rowIndex, 'barcode', event.target.value)}
                                            data-bulk-barcode-index={rowIndex}
                                            autoComplete="off"
                                            placeholder="Scan barcode"
                                            disabled={loading}
                                            title={bulkErrors[`barcode_${rowIndex}`] || 'Scan or type a unique barcode'}
                                            aria-label={`Barcode row ${rowIndex + 1}`}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            ref={element => { bulkCellRefs.current[`${rowIndex}-company`] = element; }}
                                            className="ozui-bulk-input ozui-bulk-readonly"
                                            value={bulkFormData.company}
                                            readOnly
                                            onKeyDown={(event) => handleBulkCellKeyDown(event, rowIndex, 'company')}
                                            aria-label={`Company row ${rowIndex + 1}`}
                                        />
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {bulkRows.length > BULK_PAGE_SIZE && (
                    <div className="ozui-bulk-pagination" aria-label="Bulk rows pagination">
                        <button type="button" onClick={() => setBulkPage(0)} disabled={safeBulkPage === 0}>First</button>
                        <button type="button" onClick={() => setBulkPage(page => Math.max(0, page - 1))} disabled={safeBulkPage === 0}>Previous</button>
                        <span>
                            Rows {bulkPageStart + 1}–{Math.min(bulkPageStart + BULK_PAGE_SIZE, bulkRows.length)} of {bulkRows.length}
                            {' · '}Page {safeBulkPage + 1} of {bulkPageCount}
                        </span>
                        <button type="button" onClick={() => setBulkPage(page => Math.min(bulkPageCount - 1, page + 1))} disabled={safeBulkPage >= bulkPageCount - 1}>Next</button>
                        <button type="button" onClick={() => setBulkPage(bulkPageCount - 1)} disabled={safeBulkPage >= bulkPageCount - 1}>Last</button>
                    </div>
                )}

                <div className="ozui-form-actions">
                    <button
                        type="submit"
                        className="ozui-btn ozui-btn-primary"
                        disabled={loading || !currentShop || isSubmittingRef.current || bulkRows.length === 0}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Saving {bulkRows.length} products...</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>Save {bulkRows.length || ''} Bulk Products</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
            )}
        </div>
    );
};

export default CosmeticsProducts;
